import { useEffect, useRef, useState, type CSSProperties, type MouseEvent } from "react";

type Bench = readonly [string, string];

type HoverState = { x: number; y: number; name: string; below?: boolean };

// A flying paper ball: start point plus travel, in px relative to .classroom.
type Paper = {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  arc: number;
  bounce: number;
  spin: number;
  // What is flying: a thrown paper ball unless it is a heart sent by an
  // admirer or a spark of energy from the soul of the class.
  kind?: "heart" | "spark";
};

const PAPER_THROWER = "diablo";
const PAPER_TARGET = "buli";
const PAPER_INTERVAL_MS = 280;

// Speech bubble anchored above a student's head, in px relative to .classroom.
type Bubble = {
  id: number;
  name: string;
  x: number;
  y: number;
  lines: readonly string[];
  durationMs: number;
  // Sung bubbles reveal their lines one after another, karaoke style.
  song?: boolean;
};

const BUBBLE_SPEAKERS = new Set(["vaca"]);
const BUBBLE_TEXT = "Ce ar fi sa ii dam un doi?";
const BUBBLE_DURATION_MS = 3500;

const GUITARIST = "apo";
const SONG_LINES = ["Uită-te la ea", "Ruptă e din cer și mândră ca o stea."];
// Must outlast the guitar animations in App.css (guitar-scene, 7.4s).
const SONG_DURATION_MS = 7500;

const BENCH_VIEW_WIDTH = 220;

// One football shot from the kicker's feet to the goal under the goal owner's
// bench. dx/dy is the offset between the two bench SVGs in SVG user units;
// mirrored means the ball arrives from the right, so the goal faces right.
type FootballShot = {
  id: number;
  kicker: string;
  goalOwner: string;
  dx: number;
  dy: number;
  mirrored: boolean;
};

// The two footballers: clicking either one makes him shoot at a goal that
// pops up under the other's bench. Both must sit in the left seat.
const FOOTBALL_RIVALS = ["petruescu", "clau"];
// Where the ball is juggled (FootballBall) and where the goal stands
// (FootballGoal), as x in bench SVG units.
const FOOTBALL_KICK_X = 65;
const FOOTBALL_GOAL_LEFT = 8;
const FOOTBALL_GOAL_DEPTH = 18;
// Must outlast the football animations in App.css (football-scene, 4.2s).
const FOOTBALL_DURATION_MS = 4300;

const BOOKWORM = "oti";
// Books in the pile, bottom first: width, height, cover colour, sideways
// nudge and tilt. Heights add up to the photo slot so the pile hides her.
const BOOK_PILE: readonly (readonly [number, number, string, number, number])[] = [
  [58, 8, "#8b1e2d", 0, 0],
  [50, 6, "#1f4e79", -3, -2],
  [56, 7, "#2e7d32", 2, 1.5],
  [46, 6, "#e0a526", -2, -1],
  [54, 8, "#5d3a9b", 3, 2],
  [48, 6, "#c2410c", -3, -2.5],
  [52, 7, "#0f766e", 1, 1],
];
const BOOK_FALL_STAGGER_S = 0.22;

const DAYDREAMER = "ani";

const PRINCESS = "moni mare";

const EFFORTLESS_GENIUS = "adriana";

const DJ = "mish";

const SPORTY = "alina";

const SURGEON = "razvan";

const SNOW_WHITE = "oana";

const CARICATURIST = "paul";
// Pen strokes of the caricature, in drawing order: lumpy head, jug ears,
// huge nose, beady eyes, goofy grin with one tooth, three hairs.
const CARICATURE_STROKES = [
  "M -7 -14 C -9 -24 7 -26 8 -16 C 9 -8 3 -4 -1 -4 C -6 -4 -7 -9 -7 -14 Z",
  "M -7 -17 C -12 -20 -12 -12 -7 -13 M 8 -18 C 13 -21 13 -12 8 -13",
  "M 0 -17 C 6 -15 6 -9 0 -11",
  "M -6 -18 a 1.6 1.6 0 1 0 3.2 0 a 1.6 1.6 0 1 0 -3.2 0 M 2.3 -18.5 a 1.6 1.6 0 1 0 3.2 0 a 1.6 1.6 0 1 0 -3.2 0",
  "M -4 -8 Q 0 -4.5 5 -9 M 0 -6.6 L 0 -5 L 1.6 -5.4 L 1.6 -7",
  "M -2 -24.5 L -3 -28.5 M 0 -25 L 0 -29.5 M 2 -24.5 L 3.5 -28.5",
];
// Each stroke gets its own slot on the shared 5s loop in App.css (the page
// wipes at the end of it), so the keyframes are generated rather than written
// out six times: stroke i is drawn between 10% + i*10% and 8% later.
const CARICATURE_CSS = CARICATURE_STROKES.map((_, i) => {
  const start = 10 + i * 10;
  return `@keyframes caricature-stroke-${i} { 0%, ${start}% { stroke-dashoffset: 1; } ${start + 8}%, 100% { stroke-dashoffset: 0; } }`;
}).join("\n");

const PHYSICIST = "varutz";
const MATHEMATICIAN = "LM";

const PUNK = "ozzy";
// While the punk is on, the whole classroom goes metal: bats cross the room
// and lightning flickers. Each gets its place and timing from its index (no
// randomness, so renders stay stable).
const METAL_BATS = Array.from({ length: 7 }, (_, i) => ({
  top: `${6 + ((i * 29) % 80)}%`,
  duration: `${4.5 + (i % 4) * 0.9}s`,
  delay: `${-i * 1.3}s`,
  // Every other bat crosses right to left
  reverse: i % 2 === 1,
}));
const METAL_BOLTS = Array.from({ length: 6 }, (_, i) => ({
  left: `${8 + ((i * 41) % 84)}%`,
  top: `${10 + ((i * 23) % 75)}%`,
  delay: `${-i * 0.55}s`,
}));

const EXORCIST = "buli";
const EXORCISM_TEXT = "Piei, Satană!";

const CAPTAIN = "tati";

const BALLER = "cip";
// Must outlast the basketball animations in App.css (hoops-scene, 4.2s).
const HOOPS_DURATION_MS = 4300;

const ARSONIST = "luci buci";
// Must outlast the longest fire animation in App.css (fire-burn, 6s).
const FIRE_DURATION_MS = 6500;

const TOP_LEFT_BENCH: Bench = ["adriana", "ela"];

const BENCH_ROWS: readonly (readonly Bench[])[] = [
  [
    ["ani", "oana"],
    ["delia", "moni mare"],
    ["buli", "ozzy"],
  ],
  [
    ["LM", "varutz"],
    ["vaca", "mish"],
    ["diablo", "paul"],
  ],
  [
    ["aura", "oti"],
    ["cip", "tati"],
    ["apo", "bot"],
  ],
  [
    ["karina", "ema"],
    ["dan", "tropa"],
    ["clau", "dodo"],
  ],
  [
    ["moni mica", "alina"],
    ["petruescu", "razvan"],
    ["luci buci", "radula"],
  ],
];

const GIRLS = new Set([
  "ani",
  "oana",
  "delia",
  "moni mare",
  "aura",
  "oti",
  "karina",
  "ema",
  "moni mica",
  "alina",
  "adriana",
  "ela"
]);

const isGirl = (name: string) => GIRLS.has(name);

const ALL_STUDENTS = [TOP_LEFT_BENCH, ...BENCH_ROWS.flat()].flat();
const BOYS = ALL_STUDENTS.filter((name) => !isGirl(name));

// The soul of the class, who gets everybody else going.
const CLASS_SOUL = "ema";
const CLASSMATES = ALL_STUDENTS.filter((name) => name !== CLASS_SOUL);
const SPARK_INTERVAL_MS = 260;
// Confetti over the whole classroom while her party is on: each piece gets
// a column, colour, speed and head start from its index (no randomness, so
// renders stay stable).
const CONFETTI_COLORS = ["#e6293a", "#ffeb3b", "#39b54a", "#2a7de1", "#8a3fb0", "#ff9a1f"];
const CONFETTI = Array.from({ length: 36 }, (_, i) => ({
  left: `${(i * 37) % 100}%`,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  duration: `${3.2 + (i % 5) * 0.5}s`,
  delay: `${-((i * 13) % 50) / 10}s`,
}));

const BEAUTY = "aura";
// "More than half of the boys were in love with her."
const ADMIRER_SHARE = 0.6;
// Boys who are in love with her every single time (they also get bigger
// heart-eyes), and boys who never are.
const ALWAYS_ADMIRERS = ["LM"];
const NEVER_ADMIRERS = ["ozzy"];
const HEART_INTERVAL_MS = 240;

function GirlSilhouette({ cx }: { cx: number }) {
  const skin = "#fbdcb6";
  const skinShade = "#e8b894";
  const hair = "#8a5520";
  const hairShade = "#5a3210";
  const hairHighlight = "#d8a64a";
  const blouse = "#e34a8c";
  return (
    <g transform={`translate(${cx}, 35)`}>
      {/* Shoulders / blouse — partially covered by desk later */}
      <path
        d="M -22 12 Q -26 22 -28 32 L 28 32 Q 26 22 22 12 Q 11 16 0 16 Q -11 16 -22 12 Z"
        fill={blouse}
      />
      {/* Collar V */}
      <path d="M -5 12 L 0 18 L 5 12" stroke="#ffd6e0" strokeWidth={0.6} fill="none" />
      {/* Choker (very 90s) */}
      <rect x={-4} y={8.5} width={8} height={1.2} fill="#1a1a1a" />
      {/* Neck */}
      <rect x={-3} y={9.7} width={6} height={4.5} fill={skinShade} />
      {/* Voluminous 90s hair — wider, taller silhouette */}
      <path
        d="M -19 -3 Q -26 -24 -8 -29 Q 0 -31 8 -29 Q 26 -24 19 -3 Q 23 8 21 18 L -21 18 Q -23 8 -19 -3 Z"
        fill={hair}
      />
      {/* Frosted highlights */}
      <path
        d="M -17 -10 Q -21 -20 -10 -25"
        stroke={hairHighlight}
        strokeWidth={1.4}
        fill="none"
        opacity={0.75}
        strokeLinecap="round"
      />
      <path
        d="M 17 -10 Q 21 -20 10 -25"
        stroke={hairHighlight}
        strokeWidth={1.4}
        fill="none"
        opacity={0.75}
        strokeLinecap="round"
      />
      {/* Face — slightly fairer */}
      <ellipse cx={0} cy={-7} rx={11} ry={14} fill={skin} />
      {/* Curtain bangs — parted in the middle, swept to the sides */}
      <path
        d="M -11 -16 Q -9 -22 -1 -21 L -1 -16 Q -4 -13 -11 -12 Z"
        fill={hair}
      />
      <path
        d="M 11 -16 Q 9 -22 1 -21 L 1 -16 Q 4 -13 11 -12 Z"
        fill={hair}
      />
      {/* Butterfly hair clip */}
      <g transform="translate(-13, -18)">
        <ellipse cx={-1.5} cy={0} rx={1.5} ry={1} fill="#7ec8ff" />
        <ellipse cx={1.5} cy={0} rx={1.5} ry={1} fill="#7ec8ff" />
        <circle cx={0} cy={0} r={0.45} fill="#3a6d99" />
      </g>
      {/* Cheek blush */}
      <ellipse cx={-7} cy={-4} rx={1.8} ry={1.2} fill="#f4a0b4" opacity={0.6} />
      <ellipse cx={7} cy={-4} rx={1.8} ry={1.2} fill="#f4a0b4" opacity={0.6} />
      {/* Eyes — blue/green irises */}
      <ellipse cx={-4.3} cy={-7} rx={1.3} ry={1.7} fill="#3a6a78" />
      <ellipse cx={4.3} cy={-7} rx={1.3} ry={1.7} fill="#3a6a78" />
      <ellipse cx={-4.3} cy={-7} rx={0.6} ry={1.1} fill="#1a1a1a" />
      <ellipse cx={4.3} cy={-7} rx={0.6} ry={1.1} fill="#1a1a1a" />
      <circle cx={-3.9} cy={-7.6} r={0.4} fill="#fff" />
      <circle cx={4.7} cy={-7.6} r={0.4} fill="#fff" />
      {/* Eyelash hints */}
      <path
        d="M -6 -8.4 L -5.6 -8.9 M -4 -8.7 L -4 -9.2 M 4 -8.7 L 4 -9.2 M 6 -8.4 L 5.6 -8.9"
        stroke="#1a1a1a"
        strokeWidth={0.4}
        strokeLinecap="round"
      />
      {/* Thin, over-plucked 90s brows */}
      <path
        d="M -6.8 -10.8 Q -4.5 -11.7 -2.2 -10.8"
        stroke={hairShade}
        strokeWidth={0.45}
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M 2.2 -10.8 Q 4.5 -11.7 6.8 -10.8"
        stroke={hairShade}
        strokeWidth={0.45}
        fill="none"
        strokeLinecap="round"
      />
      {/* Nose */}
      <path d="M 0 -6 L -1 -3 L 1 -3 Z" fill={skinShade} />
      {/* Glossy lips with highlight */}
      <path
        d="M -3 -1 Q 0 1.3 3 -1 Q 0 0.4 -3 -1 Z"
        fill="#ee8aaa"
        stroke="#c84a78"
        strokeWidth={0.35}
        strokeLinejoin="round"
      />
      <ellipse cx={0} cy={-0.4} rx={0.7} ry={0.25} fill="#fff" opacity={0.75} />
      {/* Hoop earrings */}
      <circle cx={-10.5} cy={-4.5} r={1.2} stroke="#ffd54f" strokeWidth={0.5} fill="none" />
      <circle cx={10.5} cy={-4.5} r={1.2} stroke="#ffd54f" strokeWidth={0.5} fill="none" />
    </g>
  );
}

function BoySilhouette({ cx }: { cx: number }) {
  const skin = "#eab896";
  const skinShade = "#cf9870";
  const hair = "#2a1908";
  const shirt = "#345b80";
  return (
    <g transform={`translate(${cx}, 35)`}>
      {/* Shoulders / shirt */}
      <path
        d="M -20 12 Q -24 22 -26 32 L 26 32 Q 24 22 20 12 Q 10 16 0 16 Q -10 16 -20 12 Z"
        fill={shirt}
      />
      {/* Collar */}
      <path d="M -5 12 L 0 17 L 5 12" stroke="#e8eef5" strokeWidth={0.6} fill="none" />
      {/* Neck */}
      <rect x={-3} y={8} width={6} height={6} fill={skinShade} />
      {/* Ears */}
      <ellipse cx={-11} cy={-6} rx={1.6} ry={2.4} fill={skin} />
      <ellipse cx={11} cy={-6} rx={1.6} ry={2.4} fill={skin} />
      {/* Face */}
      <ellipse cx={0} cy={-7} rx={11} ry={14} fill={skin} />
      {/* Hair — short, with side parting */}
      <path
        d="M -11 -11 Q -11 -22 -2 -23 Q 9 -24 11 -14 Q 11 -16 6 -16 Q 0 -14 -6 -16 Q -11 -15 -11 -11 Z"
        fill={hair}
      />
      {/* Eyes */}
      <ellipse cx={-4} cy={-7} rx={1.1} ry={1.5} fill="#1a1a1a" />
      <ellipse cx={4} cy={-7} rx={1.1} ry={1.5} fill="#1a1a1a" />
      <circle cx={-3.6} cy={-7.5} r={0.35} fill="#fff" />
      <circle cx={4.4} cy={-7.5} r={0.35} fill="#fff" />
      {/* Eyebrows */}
      <path d="M -7 -10.5 L -2 -11.2" stroke={hair} strokeWidth={0.9} strokeLinecap="round" />
      <path d="M 2 -11.2 L 7 -10.5" stroke={hair} strokeWidth={0.9} strokeLinecap="round" />
      {/* Nose */}
      <path d="M 0 -5 L -1.2 -2 L 1.2 -2 Z" fill={skinShade} />
      {/* Mouth */}
      <path
        d="M -2.5 0.5 Q 0 1.2 2.5 0.5"
        stroke="#7a3030"
        strokeWidth={0.7}
        fill="none"
        strokeLinecap="round"
      />
    </g>
  );
}

function SilhouetteSvg({ name, cx }: { name: string; cx: number }) {
  return isGirl(name) ? <GirlSilhouette cx={cx} /> : <BoySilhouette cx={cx} />;
}

function StudentSilhouette({ name, cx }: { name: string; cx: number }) {
  const base = import.meta.env.BASE_URL;
  const src = `${base}img/${encodeURIComponent(`${name}.png`)}`;
  const [failed, setFailed] = useState(false);

  // Fall back to the hand-drawn SVG silhouette when no photo is available.
  if (failed) return <SilhouetteSvg name={name} cx={cx} />;

  // A photo exists: fit it into the head/shoulders slot so the desk (drawn
  // afterwards) overlaps its lower edge, like a portrait tucked behind the bench.
  const clipId = `photo-${name.replace(/[^a-z0-9]/gi, "-")}`;
  const x = cx - 28;
  const y = 4;
  const w = 56;
  const h = 53;
  return (
    <>
      <clipPath id={clipId}>
        <rect x={x} y={y} width={w} height={h} rx={4} />
      </clipPath>
      <image
        href={src}
        x={x}
        y={y}
        width={w}
        height={h}
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#${clipId})`}
        onError={() => setFailed(true)}
      />
    </>
  );
}

// Stable per-name pseudo-random in [0, 1) so each student fidgets differently.
function nameSeed(name: string, salt: number) {
  let h = salt;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 9973;
  return h / 9973;
}

// Dangling legs under the bench. Drawn before the desk so the hips stay hidden.
// With sprint the legs pump flat out instead of dangling.
function StudentLegs({ name, cx, sprint }: { name: string; cx: number; sprint?: boolean }) {
  const girl = isGirl(name);
  const leg = girl ? "#fbdcb6" : "#3d5a80";
  const shoe = girl ? "#e34a8c" : "#1a1a1a";
  const duration = sprint ? 0.2 : 0.45 + nameSeed(name, 7) * 0.75;
  const amplitude = sprint ? 36 : 10 + nameSeed(name, 13) * 16;
  const phase = nameSeed(name, 23) * duration;
  return (
    <>
      {[-1, 1].map((side) => {
        const x = cx + side * 9;
        const style = {
          transformOrigin: `${x}px 90px`,
          animationDuration: `${duration}s`,
          // Opposite legs swing half a cycle apart
          animationDelay: `${-(phase + (side === 1 ? duration / 2 : 0))}s`,
          "--leg-swing": `${amplitude}deg`,
        } as CSSProperties;
        return (
          <g className="bench-leg" style={style} key={side}>
            <rect x={x - 3.5} y={86} width={7} height={29} rx={3} fill={leg} />
            {girl && <rect x={x - 3.5} y={106} width={7} height={9} fill="#fff" />}
            <ellipse cx={x + side * 2.5} cy={116.5} rx={7} ry={3.6} fill={shoe} />
            <ellipse cx={x + side * 5.5} cy={115.8} rx={2} ry={1} fill="#fff" opacity={0.7} />
          </g>
        );
      })}
    </>
  );
}

// Ship captain's hat that drops onto a student's head. Placement is tuned to
// where the head sits in the captain's photo (slightly left of the seat centre).
function CaptainHat({ cx }: { cx: number }) {
  return (
    // Local origin is the middle of the visor, where the hat meets the forehead.
    <g transform={`translate(${cx - 4} 13) scale(1.25)`} pointerEvents="none">
      <g className="captain-hat">
        {/* White crown */}
        <path
          d="M -14 -6 C -20 -14 -10 -18 0 -18 C 10 -18 20 -14 14 -6 Z"
          fill="#fdfdfd"
          stroke="#1a1a1a"
          strokeWidth={0.6}
        />
        {/* Navy band with gold braid */}
        <rect x={-13} y={-7} width={26} height={4.5} fill="#1b2a4a" />
        <path d="M -13 -4.2 Q 0 -2.6 13 -4.2" stroke="#e8b923" strokeWidth={0.8} fill="none" />
        {/* Visor */}
        <path d="M -14 -3 C -10 2.5 10 2.5 14 -3 C 8 -4.5 -8 -4.5 -14 -3 Z" fill="#111" />
        {/* Gold anchor badge */}
        <circle cx={0} cy={-10.5} r={3} fill="#e8b923" stroke="#8a6a0a" strokeWidth={0.4} />
        <path
          d="M 0 -12.5 L 0 -8.8 M -1.2 -11.6 L 1.2 -11.6 M -1.7 -10 Q 0 -8 1.7 -10"
          stroke="#1b2a4a"
          strokeWidth={0.5}
          fill="none"
          strokeLinecap="round"
        />
      </g>
    </g>
  );
}

// Satanic-punk makeover (the rest of the classroom going metal with him is
// handled by Classroom): neon mohawk, devil horns, a glowing
// pentagram scrawled on the desk, metal-horn hands and a circling bat.
// Head-relative pieces are tuned to where the face sits in the punk's photo.
function PunkCostume({ cx }: { cx: number }) {
  const headX = cx - 3;
  return (
    <g pointerEvents="none">
      {/* Pentagram on the desk front, drawn stroke by stroke */}
      <g transform={`translate(${cx - 6} 72)`}>
        <g className="punk-pentagram">
          <circle r={13} pathLength={1} fill="none" stroke="#ff2a2a" strokeWidth={1} />
          <path
            d="M 0 13 L -7.64 -10.52 L 12.36 4.02 L -12.36 4.02 L 7.64 -10.52 Z"
            pathLength={1}
            fill="none"
            stroke="#ff2a2a"
            strokeWidth={1}
            strokeLinejoin="round"
          />
        </g>
      </g>
      {/* Horns, one per side of the head */}
      {[-1, 1].map((side) => (
        <g transform={`translate(${headX + side * 13} 9) scale(${side} 1)`} key={side}>
          <path
            className="punk-horn"
            d="M -3 0 C 0 -6 3 -11 8 -15 C 8 -8 6 -3 3 1 Z"
            fill="#c8102e"
            stroke="#5a0010"
            strokeWidth={0.6}
          />
        </g>
      ))}
      {/* Mohawk; local origin is its root on top of the head */}
      <g transform={`translate(${headX} 7)`}>
        <g className="punk-mohawk">
          <path
            d="M -8 0 L -11 -12 L -5 -7 L -4 -19 L 0 -9 L 4 -20 L 6 -7 L 11 -13 L 8 0 Z"
            fill="#39ff14"
            stroke="#0b5a00"
            strokeWidth={0.7}
            strokeLinejoin="round"
          />
        </g>
      </g>
      {/* Metal-horn hands thrown up on both sides */}
      {[-1, 1].map((side) => (
        <g transform={`translate(${cx + side * 37} 50)`} key={side}>
          <text
            className="punk-hand"
            fontSize={14}
            textAnchor="middle"
            style={{ animationDelay: `${0.7 + (side + 1) * 0.12}s, ${1.1 + (side + 1) * 0.12}s` }}
          >
            🤘
          </text>
        </g>
      ))}
      {/* Bat doing laps over his head */}
      <g className="punk-bat">
        <g className="punk-bat-wings">
          <path d="M 0 0 L -9 -4 L -7 0 L -9.5 2 L -5 1 L -3 3 Z" fill="#1a1a1a" />
          <path d="M 0 0 L 9 -4 L 7 0 L 9.5 2 L 5 1 L 3 3 Z" fill="#1a1a1a" />
        </g>
        <ellipse rx={1.7} ry={2.6} fill="#1a1a1a" />
        <path d="M -1.5 -2 L -1.2 -4 L -0.3 -2.4 M 1.5 -2 L 1.2 -4 L 0.3 -2.4" fill="#1a1a1a" />
      </g>
    </g>
  );
}

// Glowing crucifix held out at arm's length towards the bench neighbour, the
// way you would ward off evil spirits.
function Crucifix({ cx }: { cx: number }) {
  // Point at whoever sits on the other half of the bench.
  const dir = cx < 110 ? 1 : -1;
  return (
    // Local origin is the middle of the cross; +x faces the neighbour.
    <g transform={`translate(${cx + dir * 44} 30) scale(${dir} 1) rotate(12)`} pointerEvents="none">
      <g className="crucifix">
        <circle className="crucifix-glow" r={15} fill="#fff3a0" />
        <path
          className="crucifix-rays"
          d="M 0 -19 L 0 -25 M 13.4 -13.4 L 17.7 -17.7 M 19 0 L 25 0 M 13.4 13.4 L 17.7 17.7 M -13.4 13.4 L -17.7 17.7 M -19 0 L -25 0 M -13.4 -13.4 L -17.7 -17.7"
          stroke="#ffc107"
          strokeWidth={1.2}
          strokeLinecap="round"
        />
        {/* Sleeve reaching up from behind the desk */}
        <path d="M -24 25 L -1 13" stroke="#345b80" strokeWidth={5.5} strokeLinecap="round" />
        <path
          d="M -1.8 -14 L 1.8 -14 L 1.8 -8 L 8 -8 L 8 -4.4 L 1.8 -4.4 L 1.8 13 L -1.8 13 L -1.8 -4.4 L -8 -4.4 L -8 -8 L -1.8 -8 Z"
          fill="#8a5a2b"
          stroke="#3a2410"
          strokeWidth={0.6}
          strokeLinejoin="round"
        />
        <path d="M 0 -12.5 L 0 7 M -6.5 -6.2 L 6.5 -6.2" stroke="#e8b923" strokeWidth={0.7} />
        {/* Fist gripping the foot of the cross */}
        <ellipse cx={0} cy={11.5} rx={4.2} ry={3.4} fill="#eab896" stroke="#cf9870" strokeWidth={0.5} />
        <path d="M -2.6 10 L -2.6 13 M 0 9.6 L 0 13.4 M 2.6 10 L 2.6 13" stroke="#cf9870" strokeWidth={0.4} />
      </g>
    </g>
  );
}

// Books rain down one by one and stack up on the desk until the student is
// buried, with a last one landing open on top like a little roof.
function BookPile({ cx }: { cx: number }) {
  let top = 55; // desk surface; each book lands on the one before
  return (
    <g pointerEvents="none">
      {BOOK_PILE.map(([w, h, color, dx, tilt], i) => {
        const base = top;
        top -= h;
        return (
          <g transform={`translate(${cx + dx} ${base}) rotate(${tilt})`} key={i}>
            <g className="book-fall" style={{ animationDelay: `${i * BOOK_FALL_STAGGER_S}s` }}>
              <rect
                x={-w / 2}
                y={-h}
                width={w}
                height={h}
                rx={1}
                fill={color}
                stroke="#1a1a1a"
                strokeWidth={0.5}
              />
              {/* Spine bands and title plate */}
              <path
                d={`M ${-w / 2 + 4} ${-h} L ${-w / 2 + 4} 0 M ${w / 2 - 4} ${-h} L ${w / 2 - 4} 0`}
                stroke="#f5e6b8"
                strokeWidth={0.6}
                opacity={0.8}
              />
              <rect x={-w / 5} y={-h / 2 - 1} width={(w * 2) / 5} height={2} fill="#f5e6b8" opacity={0.85} />
            </g>
          </g>
        );
      })}
      {/* Open book, face down on top of the pile */}
      <g transform={`translate(${cx} ${top + 1})`}>
        <g
          className="book-fall"
          style={{ animationDelay: `${BOOK_PILE.length * BOOK_FALL_STAGGER_S + 0.15}s` }}
        >
          <path d="M -17 2 L 0 -7 L 17 2 L 17 4.5 L 0 -4.5 L -17 4.5 Z" fill="#fdfbf3" stroke="#1a1a1a" strokeWidth={0.4} />
          <path
            d="M -18 1 L 0 -9 L 18 1 L 18 3 L 0 -7 L -18 3 Z"
            fill="#b3261e"
            stroke="#1a1a1a"
            strokeWidth={0.5}
            strokeLinejoin="round"
          />
        </g>
      </g>
    </g>
  );
}

// Physics prodigy: Newton's apple bonks him on the head, then electrons start
// orbiting it, formulas drift off, and his national olympiad haul shows up —
// three gold medals hanging off the desk and a trophy standing on it.
function PhysicsGenius({ cx }: { cx: number }) {
  // Trophy and formulas go on the inner side, towards the middle of the bench.
  const inner = cx < 110 ? 1 : -1;
  const orbit = "M 34 0 A 34 12 0 1 1 -34 0 A 34 12 0 1 1 34 0";
  return (
    <g pointerEvents="none">
      {/* Atom around his head */}
      <g transform={`translate(${cx} 28)`}>
        <g className="orbit-on">
          {[0, 60, 120].map((angle, i) => (
            <g transform={`rotate(${angle})`} key={angle}>
              <path d={orbit} fill="none" stroke="#2a7de1" strokeWidth={0.7} opacity={0.65} />
              <circle r={2.4} fill="#ffd54f" stroke="#b8860b" strokeWidth={0.5}>
                <animateMotion
                  dur={`${1.3 + i * 0.35}s`}
                  begin={`${-i * 0.4}s`}
                  repeatCount="indefinite"
                  path={orbit}
                />
              </circle>
            </g>
          ))}
        </g>
      </g>
      <FloatingFormulas
        items={[
          ["E=mc²", cx + inner * 46, 36],
          ["F=ma", cx - inner * 34, 30],
          ["ΔE=hν", cx + inner * 40, 14],
        ]}
      />
      <OlympiadPrizes cx={cx} trophyX={cx + inner * 42} />
      {/* Newton's apple; local origin is where it hits the top of his head */}
      <g transform={`translate(${cx} 3)`}>
        <g className="head-bonk" style={{ "--bonk-dir": -inner } as CSSProperties}>
          <circle cx={-1.6} cy={0} r={3.6} fill="#d62828" />
          <circle cx={1.6} cy={0} r={3.6} fill="#d62828" />
          <path d="M 0 -3 L 0.8 -6" stroke="#5a3210" strokeWidth={0.8} strokeLinecap="round" />
          <path d="M 0.8 -5.5 C 3 -7.5 5 -6.5 5 -4.5 C 3 -4 1.5 -4.5 0.8 -5.5 Z" fill="#2e7d32" />
          <circle cx={-2.6} cy={-1.4} r={0.9} fill="#fff" opacity={0.6} />
        </g>
      </g>
    </g>
  );
}

// Maths prodigy, the sibling of PhysicsGenius: a big π bonks him on the head,
// then maths symbols orbit it, formulas drift off, and the olympiad medals
// and trophy show up.
function MathGenius({ cx }: { cx: number }) {
  const inner = cx < 110 ? 1 : -1;
  const orbit = "M 34 0 A 34 12 0 1 1 -34 0 A 34 12 0 1 1 34 0";
  return (
    <g pointerEvents="none">
      {/* Symbols circling his head */}
      <g transform={`translate(${cx} 28)`}>
        <g className="orbit-on">
          {(
            [
              ["π", 0, "#6a1b9a"],
              ["∑", 60, "#c2410c"],
              ["√", 120, "#0f766e"],
            ] as const
          ).map(([symbol, angle, color], i) => (
            <g transform={`rotate(${angle})`} key={symbol}>
              <path
                d={orbit}
                fill="none"
                stroke={color}
                strokeWidth={0.6}
                strokeDasharray="1.5 2"
                opacity={0.6}
              />
              {/* Counter-rotated so the symbol stays upright on its tilted orbit */}
              <g>
                <animateMotion
                  dur={`${2.2 + i * 0.5}s`}
                  begin={`${-i * 0.7}s`}
                  repeatCount="indefinite"
                  path={orbit}
                />
                <text
                  transform={`rotate(${-angle})`}
                  fontFamily="Georgia, serif"
                  fontWeight={700}
                  fontSize={9}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={color}
                  stroke="#fff"
                  strokeWidth={1.6}
                  paintOrder="stroke"
                >
                  {symbol}
                </text>
              </g>
            </g>
          ))}
        </g>
      </g>
      <FloatingFormulas
        items={[
          ["a²+b²=c²", cx + inner * 38, 46],
          ["π²/6", cx - inner * 40, 26],
          ["∫eˣdx=eˣ", cx + inner * 36, 22],
        ]}
      />
      {/* Trophy on the outer side, so it clears the physicist's next door */}
      <OlympiadPrizes cx={cx} trophyX={cx - inner * 42} />
      {/* Falling π; local origin is where it hits the top of his head */}
      <g transform={`translate(${cx} 3)`}>
        <g className="head-bonk" style={{ "--bonk-dir": -inner } as CSSProperties}>
          <text
            y={3}
            fontFamily="Georgia, serif"
            fontWeight={700}
            fontSize={16}
            textAnchor="middle"
            fill="#6a1b9a"
            stroke="#fff"
            strokeWidth={1.4}
            paintOrder="stroke"
          >
            π
          </text>
        </g>
      </g>
    </g>
  );
}

// One puffy cartoon cloud centred on the local origin: a blue-grey silhouette
// with a slightly smaller white copy on top, which leaves just an outline.
function Cloud() {
  const puffs = [
    [-10, 0, 7.6],
    [0, -5, 9.6],
    [10, 0, 7.6],
    [-3, 3, 7],
    [4, 3, 7],
  ];
  return (
    <>
      {puffs.map(([x, y, r], i) => (
        <circle cx={x} cy={y} r={r} fill="#9db7d5" key={i} />
      ))}
      <rect x={-16.5} y={0} width={33} height={10} rx={5} fill="#9db7d5" />
      {puffs.map(([x, y, r], i) => (
        <circle cx={x} cy={y} r={r - 0.7} fill="#fff" key={i} />
      ))}
      <rect x={-15.8} y={0.7} width={31.6} height={8.6} rx={4.3} fill="#fff" />
    </>
  );
}

// Head in the clouds: clouds drift in and park over the top of the student's
// head, dizzy stars and a bird circle above, and puzzled "?"s float away.
function HeadInClouds({ cx }: { cx: number }) {
  const inner = cx < 110 ? 1 : -1;
  const orbit = "M 24 0 A 24 6 0 1 1 -24 0 A 24 6 0 1 1 24 0";
  // Settled position, size, which side it drifts in from, and its delay.
  const clouds = [
    [cx - 15, 14, 1.05, -1, 0],
    [cx + 16, 17, 0.95, 1, 0.35],
    [cx, 5, 1.3, -1, 0.7],
  ];
  return (
    <g pointerEvents="none">
      {clouds.map(([x, y, size, side, delay], i) => (
        <g transform={`translate(${x} ${y}) scale(${size})`} key={i}>
          <g
            className="cloud-drift"
            style={
              {
                "--cloud-from": `${side * 130}px`,
                animationDelay: `${delay}s, ${delay + 1.2 + i * 0.4}s`,
              } as CSSProperties
            }
          >
            <Cloud />
          </g>
        </g>
      ))}
      {/* Cartoon dizziness circling above the clouds */}
      <g transform={`translate(${cx} -8)`}>
        <g className="orbit-on" style={{ animationDelay: "1.6s" }}>
          {["✦", "🐤", "✦"].map((dizzy, i) => (
            <text
              fontSize={i === 1 ? 8 : 9}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#ffc107"
              stroke="#b8860b"
              strokeWidth={0.3}
              key={i}
            >
              <animateMotion dur="1.8s" begin={`${-i * 0.6}s`} repeatCount="indefinite" path={orbit} />
              {dizzy}
            </text>
          ))}
        </g>
      </g>
      <FloatingFormulas
        items={[
          ["?", cx + inner * 32, 22],
          ["??", cx - inner * 32, 28],
          ["hm?", cx + inner * 36, 42],
        ]}
      />
    </g>
  );
}

// Princess mode: a jewelled crown drops onto her head and sparkles, a magic
// wand waves on the outer side, and a hopeful frog prince hops on the desk on
// the inner side, begging for a kiss. Crown placement is tuned to where the
// head sits in the princess's photo (a little left of and below the slot top).
function Princess({ cx }: { cx: number }) {
  const inner = cx < 110 ? 1 : -1;
  const frogX = cx + inner * 42;
  return (
    <g pointerEvents="none">
      {/* Crown; local origin is the middle of its base */}
      <g transform={`translate(${cx - 3} 13)`}>
        <g className="princess-crown">
          <path
            d="M -9 0 L -10.5 -9 L -5 -4 L 0 -11.5 L 5 -4 L 10.5 -9 L 9 0 Z"
            fill="#f2c230"
            stroke="#8a6a0a"
            strokeWidth={0.5}
            strokeLinejoin="round"
          />
          <rect x={-9} y={-2.4} width={18} height={2.4} fill="#d9a514" stroke="#8a6a0a" strokeWidth={0.3} />
          <circle cx={-10.5} cy={-9} r={1.2} fill="#e34a8c" />
          <circle cx={0} cy={-11.5} r={1.4} fill="#e34a8c" />
          <circle cx={10.5} cy={-9} r={1.2} fill="#e34a8c" />
          <circle cx={0} cy={-1.2} r={1} fill="#c8102e" />
          <circle cx={-5} cy={-1.2} r={0.7} fill="#2a7de1" />
          <circle cx={5} cy={-1.2} r={0.7} fill="#2a7de1" />
          {[
            [-15, -8, "0.9s"],
            [13, -13, "1.3s"],
            [2, -17, "1.7s"],
          ].map(([x, y, delay], i) => (
            <text
              className="prize-sparkle"
              x={x}
              y={y}
              fontSize={6}
              fill="#ffc107"
              style={{ animationDelay: delay as string }}
              key={i}
            >
              ✦
            </text>
          ))}
        </g>
      </g>
      {/* Magic wand; local origin is the bottom of the handle, at desk level */}
      <g transform={`translate(${cx - inner * 30} 56) scale(${-inner} 1)`}>
        <g className="princess-wand">
          <path d="M 0 0 L 8 -22" stroke="#e34a8c" strokeWidth={1.5} strokeLinecap="round" />
          <path d="M 0 0 L 2 -5.5" stroke="#fff" strokeWidth={1.5} strokeLinecap="round" />
          <text
            x={8}
            y={-23}
            fontSize={11}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#ffd54f"
            stroke="#b8860b"
            strokeWidth={0.4}
          >
            ★
          </text>
          <text className="prize-sparkle" x={14} y={-27} fontSize={5} fill="#e34a8c" style={{ animationDelay: "1.9s" }}>
            ✦
          </text>
          <text className="prize-sparkle" x={-1} y={-25} fontSize={4} fill="#e34a8c" style={{ animationDelay: "2.3s" }}>
            ✦
          </text>
        </g>
      </g>
      {/* Frog prince; local origin is where he sits on the desk, facing her */}
      <g transform={`translate(${frogX} 57) scale(${-inner} 1)`}>
        <g className="princess-frog">
          {/* Back legs */}
          <ellipse cx={-6} cy={-2} rx={3.4} ry={2.2} fill="#3f9b3f" stroke="#1f5a1f" strokeWidth={0.4} />
          <ellipse cx={6} cy={-2} rx={3.4} ry={2.2} fill="#3f9b3f" stroke="#1f5a1f" strokeWidth={0.4} />
          <ellipse cx={0} cy={-5} rx={7} ry={5} fill="#5cc45c" stroke="#1f5a1f" strokeWidth={0.5} />
          <ellipse cx={0} cy={-3.6} rx={4.4} ry={2.8} fill="#c9f0b0" />
          {/* Bulging eyes, looking her way */}
          <circle cx={-3.4} cy={-10} r={2.4} fill="#fff" stroke="#1f5a1f" strokeWidth={0.5} />
          <circle cx={3.4} cy={-10} r={2.4} fill="#fff" stroke="#1f5a1f" strokeWidth={0.5} />
          <circle cx={-2.6} cy={-10} r={1} fill="#1a1a1a" />
          <circle cx={4.2} cy={-10} r={1} fill="#1a1a1a" />
          {/* Puckered lips */}
          <ellipse cx={6.6} cy={-6} rx={1.6} ry={1.3} fill="#e6293a" stroke="#8a1020" strokeWidth={0.3} />
          {/* His own little crown */}
          <path d="M -3 -12.5 L -3.6 -16.5 L -1.5 -14.5 L 0 -17.5 L 1.5 -14.5 L 3.6 -16.5 L 3 -12.5 Z" fill="#f2c230" stroke="#8a6a0a" strokeWidth={0.3} />
        </g>
      </g>
      <FloatingFormulas
        color="#e34a8c"
        items={[
          ["♥", frogX - inner * 8, 40],
          ["pup?", frogX + inner * 2, 30],
          ["♥", frogX - inner * 12, 24],
        ]}
      />
    </g>
  );
}

// Formulas that float up and fade around a student, looping one after another.
function FloatingFormulas({
  items,
  color = "#1b2a4a",
}: {
  items: readonly (readonly [string, number, number])[];
  color?: string;
}) {
  return (
    <>
      {items.map(([formula, x, y], i) => (
        <g transform={`translate(${x} ${y})`} key={i}>
          <text
            className="formula-float"
            fontFamily="Georgia, serif"
            fontStyle="italic"
            fontWeight={700}
            fontSize={7.5}
            textAnchor="middle"
            fill={color}
            stroke="#fff"
            strokeWidth={1.6}
            paintOrder="stroke"
            style={{ animationDelay: `${0.9 + i * 1}s` }}
          >
            {formula}
          </text>
        </g>
      ))}
    </>
  );
}

// Effortless genius with a heart of gold: she dozes (Zzz) under a halo while
// her homework writes itself and gets a 10, hearts drift off her, and her
// three national first places — philosophy (Φ), psychology (Ψ), biology (🧬)
// — hang off the desk. Halo placement is tuned to her photo.
function EffortlessGenius({ cx }: { cx: number }) {
  const inner = cx < 110 ? 1 : -1;
  // Keep the medals clear of the name plate at the outer end of the desk.
  const medalsX = cx < 110 ? [cx, cx + 14, cx + 28] : [cx - 28, cx - 14, cx];
  return (
    <g pointerEvents="none">
      <g transform={`translate(${cx} 2)`}>
        <g className="genius-halo">
          <ellipse rx={14} ry={3.6} fill="none" stroke="#ffd54f" strokeWidth={1.9} />
          <ellipse rx={14} ry={3.6} fill="none" stroke="#fff8c9" strokeWidth={0.6} />
        </g>
      </g>
      {/* Homework doing itself; local origin is the foot of the sheet */}
      <g transform={`translate(${cx + inner * 42} 56) rotate(${-4 * inner})`}>
        <g className="homework">
          <rect x={-9} y={-22} width={18} height={22} fill="#fffef6" stroke="#8f8f86" strokeWidth={0.5} />
          {[-18, -14.5, -11, -7.5, -4].map((y, i) => (
            <path
              className="homework-line"
              d={`M -6.5 ${y} L ${i === 4 ? 2 : 6.5} ${y}`}
              pathLength={1}
              stroke="#1f4e79"
              strokeWidth={0.7}
              strokeLinecap="round"
              style={{ animationDelay: `${0.9 + i * 0.45}s` }}
              key={y}
            />
          ))}
          <text
            className="homework-grade"
            y={-7}
            textAnchor="middle"
            fontFamily={COMIC_FONT}
            fontSize={11}
            fill="#e6293a"
            transform="rotate(-14)"
          >
            10
          </text>
          {/* Pencil working on its own: one group steps down the lines, the
              next sweeps across them */}
          <g className="homework-pencil-y">
            <g className="homework-pencil-x">
              <path d="M 0 0 L 1.2 -2.6 L 6.5 -11 L 8.2 -10 L 2.9 -1.6 Z" fill="#ffc107" stroke="#8a6a0a" strokeWidth={0.3} />
              <path d="M 0 0 L 0.9 -1.9 L 2.1 -1.2 Z" fill="#333" />
              <path d="M 6.5 -11 L 7.3 -12.3 L 9 -11.3 L 8.2 -10 Z" fill="#f48fb1" />
            </g>
          </g>
        </g>
      </g>
      <FloatingFormulas
        color="#5d3a9b"
        items={[
          ["z", cx + inner * 24, 26],
          ["Z", cx + inner * 30, 18],
          ["Z", cx + inner * 36, 10],
        ]}
      />
      <FloatingFormulas
        color="#e34a8c"
        items={[
          ["♥", cx - inner * 34, 36],
          ["♥", cx - inner * 41, 22],
          ["♥", cx - inner * 31, 14],
        ]}
      />
      {["Φ", "Ψ", "🧬"].map((subject, i) => (
        <Medal x={medalsX[i]} index={i} label={subject} key={subject} />
      ))}
    </g>
  );
}

// Caricaturist at work: an artist's beret drops onto his head and a sketch
// pad on the desk fills, stroke by stroke, with a goofy caricature; he gets a
// laugh, the page wipes and he starts over. Beret placement is tuned to his
// photo (head top a little below the slot top).
function Caricaturist({ cx }: { cx: number }) {
  const inner = cx < 110 ? 1 : -1;
  return (
    <g pointerEvents="none">
      <style>{CARICATURE_CSS}</style>
      {/* Beret; local origin is the middle of its band */}
      <g transform={`translate(${cx} 14)`}>
        <g className="artist-beret">
          <path d="M 1 -10.5 L 2.2 -13.5" stroke="#5a0010" strokeWidth={1.1} strokeLinecap="round" />
          <path
            d="M -11 0 C -15 -5 -9 -11 1 -11 C 11 -11 16 -6 12 -1 C 8 1 -6 1.5 -11 0 Z"
            fill="#c8102e"
            stroke="#5a0010"
            strokeWidth={0.5}
          />
          <path d="M -10.5 0 C -5 1.7 7 1.5 11.8 -0.8" stroke="#5a0010" strokeWidth={1.1} fill="none" strokeLinecap="round" />
        </g>
      </g>
      {/* Sketch pad standing on the desk; local origin is the foot of the page */}
      <g transform={`translate(${cx + inner * 44} 56) rotate(${3 * inner})`}>
        <g className="caricature-pad">
          <rect x={-14.5} y={-33} width={29} height={33} fill="#8a5a2b" stroke="#3a2410" strokeWidth={0.5} rx={1} />
          <rect x={-13} y={-31.5} width={26} height={30} fill="#fffef6" />
          <g className="caricature-drawing">
            {CARICATURE_STROKES.map((d, i) => (
              <path
                d={d}
                pathLength={1}
                fill="none"
                stroke="#1a1a1a"
                strokeWidth={0.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={1}
                strokeDashoffset={1}
                style={{ animation: `caricature-stroke-${i} 5s linear infinite` }}
                key={i}
              />
            ))}
          </g>
          {/* Pencil tracing the strokes */}
          <g className="caricature-pencil">
            <path d="M 0 0 L 1.2 -2.6 L 6.5 -11 L 8.2 -10 L 2.9 -1.6 Z" fill="#ffc107" stroke="#8a6a0a" strokeWidth={0.3} />
            <path d="M 0 0 L 0.9 -1.9 L 2.1 -1.2 Z" fill="#333" />
            <path d="M 6.5 -11 L 7.3 -12.3 L 9 -11.3 L 8.2 -10 Z" fill="#f48fb1" />
          </g>
          <text
            className="caricature-laugh"
            y={-36}
            textAnchor="middle"
            fontFamily={COMIC_FONT}
            fontSize={9}
            fill="#ffeb3b"
            stroke="#1a1a1a"
            strokeWidth={1.6}
            paintOrder="stroke"
          >
            HA HA!
          </text>
        </g>
      </g>
    </g>
  );
}

// One turntable record centred on the local origin. `scratch` makes it jerk
// back and forth under a hand instead of spinning.
function DjRecord({ x, label, scratch }: { x: number; label: string; scratch?: boolean }) {
  return (
    <g transform={`translate(${x} -7.5)`}>
      <g className={scratch ? "dj-record dj-record--scratch" : "dj-record"}>
        <circle r={6.4} fill="#111" stroke="#444" strokeWidth={0.4} />
        <circle r={4.6} fill="none" stroke="#333" strokeWidth={0.4} />
        <circle r={3.4} fill="none" stroke="#333" strokeWidth={0.4} />
        <circle r={2.3} fill={label} />
        {/* Off-centre marks so the rotation is visible */}
        <path d="M 0 -2.3 L 0 -6.2" stroke="#fff" strokeWidth={0.6} opacity={0.75} />
        <circle cx={0.9} cy={0.6} r={0.5} fill="#fff" />
      </g>
      {scratch && (
        <ellipse className="dj-hand" cx={1} cy={-2} rx={3} ry={2.2} fill="#eab896" stroke="#cf9870" strokeWidth={0.4} />
      )}
    </g>
  );
}

// DJ set: headphones drop onto his head, a pair of decks with a mixer lands
// on the desk in front of him — one record spinning, the other being
// scratched — an equaliser pumps on the desk front and notes fly off.
// Headphone placement is tuned to where the head sits in the DJ's photo.
function DjSet({ cx }: { cx: number }) {
  const equalizer = ["#39ff14", "#39ff14", "#ffeb3b", "#ffeb3b", "#ff9a1f", "#e6293a", "#e6293a"];
  // Keep the equaliser clear of the name plate at the outer end of the desk.
  const equalizerX = cx < 110 ? cx - 8 : cx - 28;
  return (
    <g pointerEvents="none">
      {/* Equaliser bars on the desk front; each grows up from the shelf */}
      {equalizer.map((color, i) => (
        <g transform={`translate(${equalizerX + i * 6} 85)`} key={i}>
          <rect
            className="dj-eq-bar"
            y={-22}
            width={4}
            height={22}
            fill={color}
            style={{ animationDuration: `${0.32 + ((i * 7) % 5) * 0.07}s`, animationDelay: `${-i * 0.13}s` }}
          />
        </g>
      ))}
      {/* Headphones; local origin is midway between the ears */}
      <g transform={`translate(${cx + 1.5} 31)`}>
        <g className="dj-headphones">
          <path d="M -15.5 0 C -17 -35 17 -35 15.5 0" fill="none" stroke="#1a1a1a" strokeWidth={2.4} strokeLinecap="round" />
          <path d="M -13 -14 C -10 -29 10 -29 13 -14" fill="none" stroke="#555" strokeWidth={0.6} />
          <ellipse cx={-15.5} cy={1} rx={3.8} ry={6.2} fill="#1a1a1a" stroke="#000" strokeWidth={0.4} />
          <ellipse cx={15.5} cy={1} rx={3.8} ry={6.2} fill="#1a1a1a" stroke="#000" strokeWidth={0.4} />
          <ellipse cx={-16.2} cy={1} rx={1.6} ry={3.6} fill="#e6293a" />
          <ellipse cx={16.2} cy={1} rx={1.6} ry={3.6} fill="#e6293a" />
        </g>
      </g>
      {/* Decks; local origin is the middle of the console's bottom edge */}
      <g transform={`translate(${cx} 57)`}>
        <g className="dj-decks">
          <rect x={-30} y={-16} width={60} height={16} rx={1.5} fill="#2b2b2b" stroke="#000" strokeWidth={0.5} />
          <rect x={-30} y={-16} width={60} height={1.6} rx={0.8} fill="#555" />
          <DjRecord x={-19} label="#39b54a" />
          <DjRecord x={19} label="#e34a8c" scratch />
          {/* Mixer: three channel faders and a crossfader */}
          <rect x={-8.5} y={-14} width={17} height={12.5} rx={0.8} fill="#111" />
          {[-5, 0, 5].map((x, i) => (
            <g key={x}>
              <path d={`M ${x} -12.5 L ${x} -6`} stroke="#555" strokeWidth={0.5} />
              <rect
                className="dj-fader"
                x={x - 1.4}
                y={-10.2}
                width={2.8}
                height={1.6}
                fill="#ddd"
                style={{ animationDuration: `${0.5 + i * 0.23}s`, animationDelay: `${-i * 0.2}s` }}
              />
            </g>
          ))}
          <path d="M -6 -3.6 L 6 -3.6" stroke="#555" strokeWidth={0.5} />
          <rect className="dj-crossfader" x={-1.1} y={-4.9} width={2.2} height={2.6} fill="#e6293a" />
        </g>
      </g>
      {[
        ["♫", cx - 34, 30, "#e6293a"],
        ["♪", cx + 30, 26, "#1b2a4a"],
        ["♬", cx - 28, 12, "#8a3fb0"],
        ["♪", cx + 36, 44, "#0f766e"],
      ].map(([note, x, y, color], i) => (
        <g transform={`translate(${x} ${y})`} key={i}>
          <text
            className="guitar-note"
            fontSize={11}
            fill={color as string}
            style={{ animationDelay: `${1 + i * 0.5}s` }}
          >
            {note}
          </text>
        </g>
      ))}
    </g>
  );
}

// The class beauty: she sparkles, and love letters pile up on the desk beside
// her. The hearts flying in from her admirers are HTML, handled by Classroom.
function BeautyQueen({ cx }: { cx: number }) {
  const inner = cx < 110 ? 1 : -1;
  return (
    <g pointerEvents="none">
      {[
        [cx - 30, 14, 7, "0.2s"],
        [cx + 27, 9, 6, "0.6s"],
        [cx + 30, 40, 5, "1s"],
        [cx - 27, 44, 6, "1.4s"],
        [cx - 2, 3, 5, "0.8s"],
      ].map(([x, y, size, delay], i) => (
        <text
          className="prize-sparkle"
          x={x}
          y={y}
          fontSize={size}
          textAnchor="middle"
          fill="#ffc107"
          stroke="#fff"
          strokeWidth={0.8}
          paintOrder="stroke"
          style={{ animationDelay: delay as string }}
          key={i}
        >
          ✦
        </text>
      ))}
      {/* Love letters, dropped one on top of another; each origin is the
          middle of the envelope's bottom edge */}
      {[
        [0, 0, -5],
        [-3, -4, 9],
        [2, -8, -3],
      ].map(([dx, dy, tilt], i) => (
        <g transform={`translate(${cx + inner * 42 + dx} ${56 + dy}) rotate(${tilt})`} key={i}>
          <g className="book-fall" style={{ animationDelay: `${0.6 + i * 0.5}s` }}>
            <rect x={-8.5} y={-11} width={17} height={11} fill="#fff8f0" stroke="#b98a8a" strokeWidth={0.5} />
            <path d="M -8.5 -11 L 0 -4.5 L 8.5 -11" fill="none" stroke="#b98a8a" strokeWidth={0.5} />
            <text y={-2.6} fontSize={5} textAnchor="middle" fill="#e6293a">
              ♥
            </text>
          </g>
        </g>
      ))}
    </g>
  );
}

// Heart-eyes over the head of a boy who has fallen for the class beauty;
// the die-hard admirers get a much bigger pair.
function LoveStruck({ cx, name }: { cx: number; name: string }) {
  const dieHard = ALWAYS_ADMIRERS.includes(name);
  return (
    <g transform={`translate(${cx + (dieHard ? 22 : 19)} ${dieHard ? 6 : 9})`} pointerEvents="none">
      <text
        className="lovestruck"
        fontSize={dieHard ? 24 : 12}
        textAnchor="middle"
        dominantBaseline="central"
      >
        😍
      </text>
    </g>
  );
}

// One of the seven dwarfs; local origin is between his boots.
function Dwarf({ color }: { color: string }) {
  return (
    <>
      <ellipse cx={-1.8} cy={-0.6} rx={2} ry={1.1} fill="#3a2410" />
      <ellipse cx={1.8} cy={-0.6} rx={2} ry={1.1} fill="#3a2410" />
      <rect x={-3.2} y={-8.5} width={6.4} height={7.5} rx={1.6} fill={color} stroke="#1a1a1a" strokeWidth={0.3} />
      <circle cy={-10.5} r={2.9} fill="#f6cfa8" stroke="#1a1a1a" strokeWidth={0.3} />
      <path d="M -2.9 -10 Q 0 -2.5 2.9 -10 Q 0 -8.4 -2.9 -10 Z" fill="#fff" stroke="#b9b9b9" strokeWidth={0.3} />
      <circle cx={-1} cy={-11.2} r={0.4} fill="#1a1a1a" />
      <circle cx={1} cy={-11.2} r={0.4} fill="#1a1a1a" />
      <circle cy={-10} r={0.85} fill="#f29b9b" />
      <path d="M -3.3 -12.3 L 1 -19.5 L 3.3 -12.3 Z" fill={color} stroke="#1a1a1a" strokeWidth={0.3} strokeLinejoin="round" />
    </>
  );
}

const DWARF_COLORS = ["#c8102e", "#2a7de1", "#2e7d32", "#e0a526", "#8a3fb0", "#c2410c", "#0f766e"];

// Albă ca Zăpada: the palest girl in class as Snow White. Snow falls softly
// around her, a bitten apple sits on the desk, a bluebird keeps her company,
// and the seven dwarfs march on the spot under the bench.
function SnowWhite({ cx }: { cx: number }) {
  const inner = cx < 110 ? 1 : -1;
  // The dwarfs line up in the gap between the two pairs of dangling legs.
  const dwarfsX = 110 - 3 * 9.5;
  return (
    <g pointerEvents="none">
      {DWARF_COLORS.map((color, i) => (
        <g transform={`translate(${dwarfsX + i * 9.5} 121)`} key={color}>
          <g
            className="snow-dwarf"
            style={{ animationDelay: `${0.5 + i * 0.12}s, ${0.85 + i * 0.12}s` }}
          >
            <Dwarf color={color} />
          </g>
        </g>
      ))}
      <g transform="translate(110 98) rotate(-5)">
        <text
          className="snow-heigh-ho"
          fontFamily={COMIC_FONT}
          fontSize={7}
          textAnchor="middle"
          fill="#ffeb3b"
          stroke="#1a1a1a"
          strokeWidth={1.4}
          paintOrder="stroke"
        >
          Hei-ho! Hei-ho!
        </text>
      </g>
      {/* Snowflakes drifting down around her */}
      {[-30, -17, -4, 9, 22, 32].map((dx, i) => (
        <g transform={`translate(${cx + dx} 0)`} key={dx}>
          <text
            className="snow-flake"
            fontSize={5 + (i % 3) * 1.5}
            textAnchor="middle"
            fill="#8ec5ff"
            stroke="#fff"
            strokeWidth={0.6}
            paintOrder="stroke"
            style={{ animationDuration: `${2.6 + (i % 4) * 0.5}s`, animationDelay: `${-i * 0.7}s` }}
          >
            ❄
          </text>
        </g>
      ))}
      {/* Bluebird hovering by her shoulder */}
      <g transform={`translate(${cx - inner * 26} 22)`}>
        <text className="snow-bird" fontSize={10} textAnchor="middle" dominantBaseline="central">
          🐦
        </text>
      </g>
      {/* The apple, one bite taken; local origin is where it rests on the desk */}
      <g transform={`translate(${cx + inner * 42} 57)`}>
        <g className="snow-apple">
          <circle cx={-2.6} cy={-6} r={5} fill="#d62828" />
          <circle cx={2.6} cy={-6} r={5} fill="#d62828" />
          <circle cx={-4} cy={-8} r={1.3} fill="#fff" opacity={0.55} />
          {/* Bite */}
          <circle cx={7} cy={-7} r={3.2} fill="#fff8e7" stroke="#d62828" strokeWidth={0.4} />
          <path d="M 0 -10 L 0.8 -14" stroke="#5a3210" strokeWidth={1} strokeLinecap="round" />
          <path d="M 0.8 -13.4 C 3.5 -16 6 -14.8 6 -12.4 C 3.6 -11.6 1.8 -12.2 0.8 -13.4 Z" fill="#2e7d32" />
        </g>
      </g>
    </g>
  );
}

// Dead-serious surgeon who never liked jokes: scrub cap and mask go on, and
// on the operating table beside him he takes a scalpel to a "HA HA" and cuts
// it clean in two. The heart monitor on the desk front flatlines, he rules
// "Nu-i amuzant.", and the next joke is wheeled in. Cap and mask placement
// are tuned to where the face sits in his photo.
function Surgeon({ cx }: { cx: number }) {
  const inner = cx < 110 ? 1 : -1;
  const headX = cx + 2.5;
  // Keep the monitor clear of the name plate at the outer end of the desk.
  const monitorX = cx < 110 ? cx - 6 : cx - 38;
  const beat = "M 2 0 L 9 0 L 11 -3 L 13 0 L 16 0 L 18 -8 L 21 6 L 23 0 L 30 0 L 33 -4 L 36 0 L 42 0";
  return (
    <g pointerEvents="none">
      {/* Heart monitor on the desk front; local origin is its left edge at
          the baseline of the trace */}
      <g transform={`translate(${monitorX} 76)`}>
        <g className="surgeon-pop">
          <rect x={0} y={-11} width={44} height={20} rx={2} fill="#0b1f14" stroke="#1a1a1a" strokeWidth={0.6} />
          <g className="surgeon-alive">
            <path d={beat} fill="none" stroke="#1d5a36" strokeWidth={0.6} />
            <path
              className="surgeon-beat"
              d={beat}
              pathLength={1}
              fill="none"
              stroke="#39ff14"
              strokeWidth={0.9}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <g className="surgeon-flatline">
            <path d="M 2 0 L 42 0" stroke="#ff3b3b" strokeWidth={0.9} strokeLinecap="round" />
            <text x={22} y={-4.5} fontSize={4} fontWeight={700} textAnchor="middle" fill="#ff3b3b" fontFamily="Arial, sans-serif">
              BEEEEP
            </text>
          </g>
        </g>
      </g>
      {/* Scrub cap; local origin is the middle of its brim */}
      <g transform={`translate(${headX} 21)`}>
        <g className="surgeon-cap">
          <path
            d="M -16 0 C -18 -10 -10 -17 0 -17 C 10 -17 18 -10 16 0 C 8 -3 -8 -3 -16 0 Z"
            fill="#2a9d8f"
            stroke="#17645b"
            strokeWidth={0.6}
          />
          <path d="M -15.6 -2.4 C -8 -5.4 8 -5.4 15.6 -2.4" fill="none" stroke="#17645b" strokeWidth={0.5} />
        </g>
      </g>
      {/* Surgical mask; local origin is the middle of its top edge */}
      <g transform={`translate(${headX - 0.5} 33)`}>
        <g className="surgeon-mask">
          <path d="M -9 1.5 L -15 -3 M 9 1.5 L 15 -3 M -9 9 L -14.5 5 M 9 9 L 14.5 5" stroke="#dfeff5" strokeWidth={0.6} />
          <rect x={-9} y={0} width={18} height={11} rx={2.5} fill="#a8d8ea" stroke="#5e9db3" strokeWidth={0.5} />
          <path d="M -8 3.5 L 8 3.5 M -8 6 L 8 6 M -8 8.5 L 8 8.5" stroke="#5e9db3" strokeWidth={0.35} />
        </g>
      </g>
      {/* Operating table; local origin is the middle of its foot on the desk */}
      <g transform={`translate(${cx + inner * 44} 56)`}>
        <g className="surgeon-pop">
          <rect x={-16} y={-4.5} width={32} height={4.5} rx={1} fill="#2a9d8f" stroke="#17645b" strokeWidth={0.5} />
          {/* The patient: one joke, cut down the middle */}
          {(["left", "right"] as const).map((half) => (
            <text
              className={`surgeon-ha surgeon-ha--${half}`}
              x={half === "left" ? -0.8 : 0.8}
              y={-6}
              textAnchor={half === "left" ? "end" : "start"}
              fontFamily={COMIC_FONT}
              fontSize={10}
              fill="#ffeb3b"
              stroke="#1a1a1a"
              strokeWidth={1.6}
              paintOrder="stroke"
              key={half}
            >
              HA
            </text>
          ))}
          {/* Scalpel; local origin is the tip of the blade */}
          <g className="surgeon-scalpel">
            <path d="M -1.2 -7.6 L 1 -8 L 2.6 -22 L 0.4 -22 Z" fill="#9aa5ab" stroke="#5f6b70" strokeWidth={0.3} />
            <path d="M 0 0 L -1.7 -7.4 L 1.2 -8 Z" fill="#eef3f5" stroke="#5f6b70" strokeWidth={0.3} />
          </g>
          <text
            className="surgeon-verdict"
            y={-24}
            textAnchor="middle"
            fontFamily="Georgia, serif"
            fontWeight={700}
            fontSize={5.5}
            fill="#1b2a4a"
            stroke="#fff"
            strokeWidth={1.4}
            paintOrder="stroke"
          >
            Nu-i amuzant.
          </text>
        </g>
      </g>
    </g>
  );
}

// The soul of the class: wherever she is, the party is. A party hat drops
// onto her head, a disco ball spins beside her and the class chants her name.
// The rest of the party — sparks flying to every classmate, confetti, the
// whole room bouncing — is HTML/CSS, handled by Classroom.
function ClassSoul({ cx, name }: { cx: number; name: string }) {
  const inner = cx < 110 ? 1 : -1;
  const chant = `${name.toUpperCase().split("").join("-")}!`;
  return (
    <g pointerEvents="none">
      {/* Party hat; local origin is the middle of its brim */}
      <g transform={`translate(${cx} 13)`}>
        <g className="party-hat">
          <path d="M -8 0 L 1.5 -21 L 8 0 Z" fill="#8a3fb0" stroke="#4a1f66" strokeWidth={0.5} strokeLinejoin="round" />
          <path d="M -5.6 -5.2 L 5.9 -6.8 M -3.2 -10.6 L 4.2 -12.2 M -0.9 -15.8 L 2.6 -16.6" stroke="#ffeb3b" strokeWidth={1.4} />
          <path d="M -8 0 Q 0 2.4 8 0" fill="none" stroke="#ffeb3b" strokeWidth={1.4} strokeLinecap="round" />
          <circle cx={1.5} cy={-21.5} r={2.6} fill="#e6293a" stroke="#8a1020" strokeWidth={0.4} />
        </g>
      </g>
      {/* Disco ball hanging on the inner side; local origin is its centre */}
      <g transform={`translate(${cx + inner * 42} 24)`}>
        <g className="party-pop">
          <path d="M 0 -9 L 0 -30" stroke="#777" strokeWidth={0.6} />
          <circle r={9} fill="#c9d3da" stroke="#6b7780" strokeWidth={0.6} />
          <path
            d="M -9 0 L 9 0 M -7.8 -4.5 L 7.8 -4.5 M -7.8 4.5 L 7.8 4.5 M 0 -9 L 0 9 M -4.5 -7.8 C -6.5 -3 -6.5 3 -4.5 7.8 M 4.5 -7.8 C 6.5 -3 6.5 3 4.5 7.8"
            fill="none"
            stroke="#6b7780"
            strokeWidth={0.4}
          />
          {[
            [-4, -4, "#ff5ad1", "0s"],
            [3, -2, "#5ad1ff", "0.3s"],
            [-2, 3, "#ffeb3b", "0.6s"],
            [5, 4, "#7dff5a", "0.9s"],
          ].map(([x, y, color, delay], i) => (
            <rect
              className="party-glint"
              x={(x as number) - 1.4}
              y={(y as number) - 1.4}
              width={2.8}
              height={2.8}
              fill={color as string}
              style={{ animationDelay: delay as string }}
              key={i}
            />
          ))}
        </g>
      </g>
      <FloatingFormulas
        color="#8a3fb0"
        items={[
          [chant, cx - inner * 30, 36],
          ["♪", cx + inner * 26, 44],
          [chant, cx - inner * 34, 16],
        ]}
      />
    </g>
  );
}

// One dumbbell held in a fist; local origin is the middle of the bar.
function Dumbbell() {
  return (
    <>
      <rect x={-8} y={-1.1} width={16} height={2.2} fill="#8a949a" />
      <rect x={-10.5} y={-4.5} width={3.6} height={9} rx={1} fill="#2b2b2b" />
      <rect x={6.9} y={-4.5} width={3.6} height={9} rx={1} fill="#2b2b2b" />
      <ellipse rx={3.4} ry={2.8} fill="#fbdcb6" stroke="#e8b894" strokeWidth={0.4} />
      <path d="M -1.7 -1.6 L -1.7 1.6 M 0 -1.9 L 0 1.9 M 1.7 -1.6 L 1.7 1.6" stroke="#e8b894" strokeWidth={0.35} />
    </>
  );
}

// Sporty, bursting with energy and friendly with everyone: a sweatband goes
// on, she pumps a dumbbell on each side, sweat and sparks of energy fly off
// her, and she waves hello across the bench. Under the desk her legs sprint
// flat out (see StudentLegs) and kick up dust. Sweatband placement is tuned
// to where the forehead sits in her photo.
function Sporty({ cx }: { cx: number }) {
  const inner = cx < 110 ? 1 : -1;
  return (
    <g pointerEvents="none">
      {/* Dust kicked up behind her running feet */}
      {[0, 1, 2].map((i) => (
        <g transform={`translate(${cx - 22} 119)`} key={i}>
          <circle className="sporty-dust" r={3.2} fill="#c9b99b" style={{ animationDelay: `${i * 0.2}s` }} />
        </g>
      ))}
      {/* Speed lines */}
      <path
        className="sporty-speed"
        d={`M ${cx - 34} 99 L ${cx - 22} 99 M ${cx - 38} 105 L ${cx - 24} 105 M ${cx - 33} 111 L ${cx - 23} 111`}
        stroke="#9aa5ab"
        strokeWidth={0.9}
        strokeLinecap="round"
      />
      {/* Sweatband; local origin is the middle of her forehead */}
      <g transform={`translate(${cx + 3.5} 20) rotate(4)`}>
        <g className="sporty-pop">
          <path d="M -11.5 -1.8 Q 0 -4.4 11.5 -1.8 L 11.5 2.6 Q 0 0 -11.5 2.6 Z" fill="#e6293a" stroke="#8a1020" strokeWidth={0.4} />
          <path d="M -11.5 0.4 Q 0 -2.2 11.5 0.4" fill="none" stroke="#fff" strokeWidth={0.9} />
        </g>
      </g>
      {/* Dumbbells, pumped in turn */}
      {[-1, 1].map((side) => (
        <g transform={`translate(${cx + side * 31} 30)`} key={side}>
          <g className="sporty-pump" style={{ animationDelay: `0.5s, ${side === 1 ? 0.85 : 1.15}s` }}>
            <Dumbbell />
          </g>
        </g>
      ))}
      {/* Sweat flying off */}
      {[
        [-17, 12, -1],
        [18, 10, 1],
        [14, 26, 1],
      ].map(([dx, y, dir], i) => (
        <g transform={`translate(${cx + 3 + dx} ${y})`} key={i}>
          <path
            className="sporty-sweat"
            d="M 0 -3 C 2 0 2.2 1.6 0 2.4 C -2.2 1.6 -2 0 0 -3 Z"
            fill="#7ec8ff"
            stroke="#3a8fcf"
            strokeWidth={0.3}
            style={{ "--sweat-dir": dir, animationDelay: `${0.8 + i * 0.35}s` } as CSSProperties}
          />
        </g>
      ))}
      {/* Friendly wave towards her bench neighbour */}
      <g transform={`translate(${cx + inner * 45} 47)`}>
        <text className="sporty-wave" fontSize={12} textAnchor="middle" dominantBaseline="central">
          👋
        </text>
      </g>
      <FloatingFormulas
        color="#e0a526"
        items={[
          ["⚡", cx - inner * 6, 2],
          ["salut!", cx + inner * 44, 30],
          ["⚡", cx - inner * 40, 16],
        ]}
      />
    </g>
  );
}

// Gold medal hanging from the desk edge at x, swinging from its ribbon.
// index staggers when it pops in and how it swings.
function Medal({ x, index, label = "1" }: { x: number; index: number; label?: string }) {
  return (
    <g transform={`translate(${x} 55)`}>
      <g
        className="prize-medal"
        style={{ animationDelay: `${1.2 + index * 0.25}s, ${1.6 + index * 0.31}s` }}
      >
        <path d="M -3 0 L -1.2 9 L 1.2 9 L 3 0 Z" fill="#c8102e" />
        <path d="M -1 0 L -0.4 9 L 0.4 9 L 1 0 Z" fill="#ffd54f" />
        <circle cy={14} r={6} fill="#f2c230" stroke="#8a6a0a" strokeWidth={0.6} />
        <circle cy={14} r={4.4} fill="none" stroke="#8a6a0a" strokeWidth={0.3} />
        <text
          y={16.6}
          textAnchor="middle"
          fontSize={7.5}
          fontWeight={700}
          fontFamily="Georgia, serif"
          fill="#7a5a00"
        >
          {label}
        </text>
      </g>
    </g>
  );
}

// National olympiad haul: three gold medals hanging off the desk in front of
// the student and a trophy standing on it at trophyX.
function OlympiadPrizes({ cx, trophyX }: { cx: number; trophyX: number }) {
  // Keep the medals clear of the name plate at the outer end of the desk.
  const medalOffsets = cx < 110 ? [-12, 2, 16] : [-26, -12, 2];
  return (
    <>
      {medalOffsets.map((dx, i) => (
        <Medal x={cx + dx} index={i} key={dx} />
      ))}
      {/* Trophy on the desk; local origin is the middle of its base */}
      <g transform={`translate(${trophyX} 57)`}>
        <g className="prize-trophy">
          <path
            d="M -6 -16 C -11 -16 -11 -10 -4.5 -10 M 6 -16 C 11 -16 11 -10 4.5 -10"
            fill="none"
            stroke="#f2c230"
            strokeWidth={1.3}
          />
          <path
            d="M -6 -18 L 6 -18 C 6 -11 3 -7 0 -7 C -3 -7 -6 -11 -6 -18 Z"
            fill="#f2c230"
            stroke="#8a6a0a"
            strokeWidth={0.5}
          />
          <rect x={-1} y={-7} width={2} height={5} fill="#d9a514" />
          <rect x={-5.5} y={-2.5} width={11} height={2.5} rx={0.6} fill="#5a3a1a" />
          <path d="M -3.5 -16.5 C -3.5 -13 -2.5 -11 -1.5 -10" stroke="#fff" strokeWidth={0.8} fill="none" opacity={0.7} />
          <text className="prize-sparkle" x={-10} y={-18} fontSize={6} fill="#ffc107">
            ✦
          </text>
          <text
            className="prize-sparkle"
            x={6}
            y={-21}
            fontSize={5}
            fill="#ffc107"
            style={{ animationDelay: "2.4s" }}
          >
            ✦
          </text>
        </g>
      </g>
    </>
  );
}

// Metal horns thrown up beside a student's head while the classroom is in
// metal mode.
function MetalHorns({ cx }: { cx: number }) {
  return (
    <g transform={`translate(${cx - 21} 12)`} pointerEvents="none">
      <text className="punk-hand" fontSize={13} textAnchor="middle" dominantBaseline="central">
        🤘
      </text>
    </g>
  );
}

// Bottle of rum standing on the desk edge next to the captain.
function RumBottle({ x }: { x: number }) {
  return (
    // Local origin is the centre of the bottle's base, so it wobbles on it.
    <g transform={`translate(${x} 58)`} pointerEvents="none">
      <g className="rum-bottle">
        <path
          d="M -5 0 L -5 -16 C -5 -20 -2 -20 -2 -23 L -2 -27 L 2 -27 L 2 -23 C 2 -20 5 -20 5 -16 L 5 0 Z"
          fill="#5a2e12"
          stroke="#2a1508"
          strokeWidth={0.5}
        />
        <rect x={-1.8} y={-29.5} width={3.6} height={3} rx={0.6} fill="#c9a36a" />
        <path d="M -3.3 -15 L -3.3 -1.5" stroke="#fff" strokeWidth={0.8} opacity={0.3} />
        <rect x={-4.2} y={-12.5} width={8.4} height={7.5} fill="#f1e3bf" />
        <text
          x={0}
          y={-7.4}
          textAnchor="middle"
          fontSize={3.2}
          fontWeight={700}
          fill="#3a2410"
          fontFamily="Georgia, serif"
        >
          RUM
        </text>
      </g>
    </g>
  );
}

// Acoustic guitar played in front of the left student, neck pointing towards
// the middle of the bench, with notes floating off it. The lyrics themselves
// are an HTML bubble handled by Classroom.
function GuitarScene() {
  return (
    <g className="guitar-scene" pointerEvents="none">
      {/* Local origin is the middle of the body; the neck runs along +x */}
      <g transform="translate(52 57) rotate(-22)">
        <g className="guitar-rock">
          <rect x={14} y={-1.8} width={31} height={3.6} fill="#5a3210" />
          <path
            d="M 20 -1.8 L 20 1.8 M 25 -1.8 L 25 1.8 M 30 -1.8 L 30 1.8 M 35 -1.8 L 35 1.8 M 40 -1.8 L 40 1.8"
            stroke="#d9c9a0"
            strokeWidth={0.4}
          />
          <rect x={44} y={-3} width={8} height={6} rx={1.2} fill="#3a2410" />
          <circle cx={46.5} cy={-3.6} r={0.8} fill="#d9c9a0" />
          <circle cx={49.5} cy={-3.6} r={0.8} fill="#d9c9a0" />
          <circle cx={46.5} cy={3.6} r={0.8} fill="#d9c9a0" />
          <circle cx={49.5} cy={3.6} r={0.8} fill="#d9c9a0" />
          <ellipse cx={-5} cy={0} rx={11} ry={9.5} fill="#c98a3c" stroke="#5a3210" strokeWidth={0.7} />
          <ellipse cx={8} cy={0} rx={8} ry={7} fill="#c98a3c" stroke="#5a3210" strokeWidth={0.7} />
          {/* Hide the seam where the two bouts overlap */}
          <ellipse cx={2.5} cy={0} rx={5} ry={5.6} fill="#c98a3c" />
          <circle cx={4} cy={0} r={3} fill="#2a1508" />
          <rect x={-10} y={-2.6} width={2} height={5.2} fill="#3a2410" />
          <path
            className="guitar-strings"
            d="M -9 -1.1 L 47 -1.1 M -9 0 L 47 0 M -9 1.1 L 47 1.1"
            stroke="#f3f3f3"
            strokeWidth={0.3}
          />
          {/* Fretting hand */}
          <ellipse cx={33} cy={0} rx={2.4} ry={3.4} fill="#eab896" stroke="#cf9870" strokeWidth={0.4} />
          {/* Strumming hand */}
          <ellipse
            className="guitar-strum"
            cx={3}
            cy={0}
            rx={2.8}
            ry={3.4}
            fill="#eab896"
            stroke="#cf9870"
            strokeWidth={0.4}
          />
        </g>
      </g>
      {[
        ["♪", 74, 38, "#e6293a"],
        ["♫", 88, 30, "#1b2a4a"],
        ["♪", 100, 40, "#8a3fb0"],
      ].map(([note, x, y, color], i) => (
        <g transform={`translate(${x} ${y})`} key={i}>
          <text
            className="guitar-note"
            fontSize={11}
            fill={color as string}
            style={{ animationDelay: `${0.6 + i * 0.7}s` }}
          >
            {note}
          </text>
        </g>
      ))}
    </g>
  );
}

const COMIC_FONT ="'Bangers', 'Impact', 'Arial Black', sans-serif";

// A basketball drops in, bounces three times on the left student's head, then
// arcs across the bench into a hoop at the far right. The path is tuned to the
// baller's photo, whose head fills the slot right up to its top edge. All
// timing lives in App.css; remount it to replay.
function HoopsScene() {
  return (
    <g className="hoops-scene" pointerEvents="none">
      {/* Nested groups: x travel, y travel, squash on impact, spin */}
      <g className="hoops-ball-x">
        <g className="hoops-ball-y">
          <g className="hoops-ball-squash">
            <g className="hoops-ball-spin">
              <circle r={6} fill="#e8742a" stroke="#3a1a08" strokeWidth={0.7} />
              <path
                d="M -6 0 L 6 0 M 0 -6 L 0 6 M -4.2 -4.2 Q -1 0 -4.2 4.2 M 4.2 -4.2 Q 1 0 4.2 4.2"
                stroke="#3a1a08"
                strokeWidth={0.6}
                fill="none"
              />
            </g>
          </g>
        </g>
      </g>
      {/* Hoop, seen from the side; local origin is the centre of the rim */}
      <g transform="translate(204 8)">
        <g className="hoops-hoop">
          <path d="M 11.2 6 L 11.2 47" stroke="#555" strokeWidth={1.5} />
          <rect x={10} y={-16} width={2.5} height={22} fill="#fff" stroke="#1a1a1a" strokeWidth={0.6} />
          <path
            className="hoops-net"
            d="M -7 1 L -4.5 11 M -3.5 2 L -2 11 M 0 2 L 0 11 M 3.5 2 L 2 11 M 7 1 L 4.5 11 M -6 5 L 6 5 M -5 8.5 L 5 8.5"
            stroke="#888"
            strokeWidth={0.5}
            fill="none"
          />
          <ellipse cx={0} cy={0} rx={7.5} ry={2} fill="none" stroke="#e6452a" strokeWidth={1.3} />
          <path d="M 7.5 0 L 10 0" stroke="#e6452a" strokeWidth={1.3} />
        </g>
      </g>
      <g transform="translate(82 2) rotate(-12)">
        <text
          className="hoops-boing"
          fontFamily={COMIC_FONT}
          fontSize={9}
          fill="#ffeb3b"
          stroke="#1a1a1a"
          strokeWidth={1.6}
          paintOrder="stroke"
        >
          BOING!
        </text>
      </g>
      <g transform="translate(172 -8) rotate(8)">
        <text
          className="hoops-swish"
          fontFamily={COMIC_FONT}
          fontSize={9}
          fill="#e6293a"
          stroke="#1a1a1a"
          strokeWidth={1.6}
          paintOrder="stroke"
          textAnchor="middle"
        >
          SWISH!
        </text>
      </g>
    </g>
  );
}

// Football stunt, split over two benches: the ball (FootballBall) bounces in
// to the footballer's feet under his bench, gets juggled twice, then is
// blasted across the classroom into a goal (FootballGoal) under the goal
// owner's bench. Both halves share one 4s timeline in App.css and must be
// mounted together; remount them to replay.
function FootballGoal({ mirrored }: { mirrored: boolean }) {
  return (
    <g className="football-scene" pointerEvents="none">
      {/* Goal seen from the side, at the left end of the bench; local origin
          is the foot of the front post. Its mouth faces left, or right when
          mirrored, so it always opens towards the incoming ball. */}
      <g
        transform={
          mirrored
            ? `translate(${FOOTBALL_GOAL_LEFT + FOOTBALL_GOAL_DEPTH} 120) scale(-1 1)`
            : `translate(${FOOTBALL_GOAL_LEFT} 120)`
        }
      >
        <g className="football-goal">
          <path
            className="football-net"
            d="M 0 -18 L 15 -15 M 0 -12 L 16 -10 M 0 -6 L 17 -5 M 5 -22.6 L 6 0 M 10 -21.2 L 12 0 M 14 -20 L 18 0"
            stroke="#8f8f8f"
            strokeWidth={0.5}
            fill="none"
          />
          <path
            d="M 0 0 L 0 -24 L 14 -20"
            stroke="#1a1a1a"
            strokeWidth={2.4}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M 0 0 L 0 -24 L 14 -20"
            stroke="#fff"
            strokeWidth={1.3}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </g>
      <g transform="translate(110 109) rotate(-6)">
        <text
          className="football-gol"
          fontFamily={COMIC_FONT}
          fontSize={13}
          fill="#39b54a"
          stroke="#1a1a1a"
          strokeWidth={1.8}
          paintOrder="stroke"
          textAnchor="middle"
        >
          GOOOL!
        </text>
      </g>
    </g>
  );
}

// The ball lives in the footballer's bench SVG and flies out of it (the bench
// SVGs don't clip) by the offset to the goal's bench, given in SVG units.
function FootballBall({ shot }: { shot: FootballShot }) {
  // The ball slams into the back of the net, then settles just inside it.
  const mouth = FOOTBALL_GOAL_LEFT + (shot.mirrored ? FOOTBALL_GOAL_DEPTH : 0);
  const into = shot.mirrored ? -1 : 1;
  const style = {
    "--shot-x-hit": `${shot.dx + mouth + into * 12}px`,
    "--shot-x-rest": `${shot.dx + mouth + into * 8}px`,
    "--goal-dy": `${shot.dy}px`,
  } as CSSProperties;
  return (
    <g className="football-scene" pointerEvents="none" style={style}>
      {/* Nested groups: x travel, y travel, spin */}
      <g className="football-ball-x">
        <g className="football-ball-y">
          <g className="football-ball-spin">
            <circle r={5} fill="#fff" stroke="#1a1a1a" strokeWidth={0.6} />
            <path d="M 0 -2 L 1.9 -0.62 L 1.18 1.62 L -1.18 1.62 L -1.9 -0.62 Z" fill="#1a1a1a" />
            <path
              d="M 0 -2 L 0 -5 M 1.9 -0.62 L 4.76 -1.55 M 1.18 1.62 L 2.94 4.05 M -1.18 1.62 L -2.94 4.05 M -1.9 -0.62 L -4.76 -1.55"
              stroke="#1a1a1a"
              strokeWidth={0.6}
            />
          </g>
        </g>
      </g>
    </g>
  );
}

// A stack of papers in the middle of the desk that gets lit with a match,
// burns and chars. All timing lives in App.css; remount it to replay.
function PaperFire() {
  return (
    // Local origin sits at the base of the flames, so the CSS scale
    // animations (default SVG transform-origin 0 0) grow up from the paper.
    <g transform="translate(100 62)" pointerEvents="none">
      <rect className="fire-paper" x={-12} y={-4} width={24} height={9} transform="rotate(-7)" />
      <rect className="fire-paper" x={-10} y={-3} width={22} height={8} transform="rotate(9)" />
      <rect className="fire-paper" x={-11} y={-3.5} width={22} height={8} transform="rotate(-2)" />
      {/* Smoke puffs */}
      {[
        [-3, -30, 3],
        [4, -33, 3.6],
        [0, -36, 4.2],
      ].map(([x, y, r], i) => (
        <g transform={`translate(${x} ${y})`} key={i}>
          <circle
            className="fire-smoke"
            r={r}
            fill="#6f6f6f"
            style={{ animationDelay: `${1.6 + i * 0.55}s` }}
          />
        </g>
      ))}
      <g className="fire-flames">
        <path
          className="fire-flame"
          d="M -10 0 C -15 -11 -8 -17 -6 -25 C -3 -19 -1 -21 0 -33 C 4 -23 8 -21 7 -27 C 12 -17 14 -9 10 0 Z"
          fill="#e6452a"
        />
        <path
          className="fire-flame fire-flame--mid"
          d="M -6 0 C -9 -9 -4 -13 -3 -19 C -1 -15 1 -17 1 -25 C 4 -17 8 -13 6 0 Z"
          fill="#ff9a1f"
        />
        <path
          className="fire-flame fire-flame--core"
          d="M -3 0 C -5 -6 -1 -9 0 -15 C 2 -9 4 -6 3 0 Z"
          fill="#ffe14d"
        />
      </g>
      {/* Lit match reaching in from the arsonist's side */}
      <g className="fire-match">
        <path d="M -11 -11 L -4 -4" stroke="#b98a4e" strokeWidth={1.2} strokeLinecap="round" />
        <circle cx={-4} cy={-4} r={1.4} fill="#b3261e" />
        <path d="M -4 -5 C -6 -7 -4.5 -8.5 -4 -10.5 C -3.2 -8.5 -2 -7 -4 -5 Z" fill="#ffb52e" />
      </g>
    </g>
  );
}

// Neutral placeholder used as the ultimate fallback when no image is found.
function GenericAvatar() {
  return (
    <svg viewBox="0 0 100 100" className="avatar-popup-figure" aria-hidden>
      <circle cx={50} cy={50} r={49} fill="#e8eaed" />
      <circle cx={50} cy={41} r={19} fill="#9aa0a6" />
      <path d="M 16 88 Q 16 60 50 60 Q 84 60 84 88 Z" fill="#9aa0a6" />
    </svg>
  );
}

// Avatar shown in the hover popup. Prefers img/<name>Avatar.png; on a missing
// file it falls back to img/genericAvatar.png, then to the inline SVG.
function AvatarImage({ name }: { name: string }) {
  const base = import.meta.env.BASE_URL;
  const studentSrc = `${base}img/${encodeURIComponent(`${name}Avatar.png`)}`;
  const genericSrc = `${base}img/genericAvatar.png`;
  const [src, setSrc] = useState(studentSrc);
  const [failed, setFailed] = useState(false);

  if (failed) return <GenericAvatar />;
  return (
    <img
      className="avatar-popup-figure"
      src={src}
      alt={name}
      draggable={false}
      onError={() => (src === genericSrc ? setFailed(true) : setSrc(genericSrc))}
    />
  );
}

function BenchView({
  leftName,
  rightName,
  onHover,
  onLeave,
  onStudentClick,
  fireId,
  captainName,
  hoopsId,
  guitarId,
  punkName,
  exorcistName,
  football,
  goal,
  bookwormName,
  physicistName,
  mathematicianName,
  daydreamerName,
  princessName,
  effortlessGeniusName,
  caricaturistName,
  djName,
  beautyName,
  admirers,
  snowWhiteName,
  surgeonName,
  classSoulName,
  metal,
  sportyName,
}: {
  // Student on this bench currently in full sporty mode, if any.
  sportyName?: string | null;
  // True while the whole classroom is in metal mode: everyone on the bench
  // throws the horns, except the punk himself (he has his own) and anyone
  // busy holding a crucifix against him.
  metal?: boolean;
  // The soul of the class, set on her bench while her party is on.
  classSoulName?: string | null;
  // Student on this bench currently operating on jokes, if any.
  surgeonName?: string | null;
  // Student on this bench currently playing Snow White, if any.
  snowWhiteName?: string | null;
  // The class beauty, set on her bench while she is being admired.
  beautyName?: string | null;
  // Boys currently in love with her; those on this bench get heart-eyes.
  admirers?: ReadonlySet<string> | null;
  // Student on this bench currently behind the DJ decks, if any.
  djName?: string | null;
  // Student on this bench currently drawing caricatures, if any.
  caricaturistName?: string | null;
  // Student on this bench currently acing everything in their sleep, if any.
  effortlessGeniusName?: string | null;
  // Student on this bench currently in princess mode, if any.
  princessName?: string | null;
  // Student on this bench currently with their head in the clouds, if any.
  daydreamerName?: string | null;
  // Student on this bench currently showing off their maths prizes, if any.
  mathematicianName?: string | null;
  // Student on this bench currently showing off their physics prizes, if any.
  physicistName?: string | null;
  // Student on this bench currently buried under a pile of books, if any.
  bookwormName?: string | null;
  leftName: string;
  rightName: string;
  // The current shot, set on the kicker's bench while the stunt plays.
  football?: FootballShot | null;
  // The current shot, set on the goal owner's bench while the stunt plays.
  goal?: FootballShot | null;
  // Student on this bench currently brandishing a crucifix, if any.
  exorcistName?: string | null;
  // Student on this bench currently in satanic-punk mode, if any.
  punkName?: string | null;
  // Set while the guitar serenade plays on this bench; a new id replays it.
  guitarId?: number | null;
  // Set while the basketball stunt plays on this bench; a new id replays it.
  hoopsId?: number | null;
  // Student on this bench currently dressed up as a ship captain, if any.
  captainName?: string | null;
  // Set while papers burn on this bench; a new id replays the animation.
  fireId?: number | null;
  onHover: (name: string, e: MouseEvent, below?: boolean) => void;
  onLeave: () => void;
  onStudentClick: (name: string) => void;
}) {
  const captainCx =
    captainName === leftName ? 60 : captainName === rightName ? 160 : null;
  const punkCx = punkName === leftName ? 60 : punkName === rightName ? 160 : null;
  const djCx = djName === leftName ? 60 : djName === rightName ? 160 : null;
  const sportyCx = sportyName === leftName ? 60 : sportyName === rightName ? 160 : null;
  const classSoulCx =
    classSoulName === leftName ? 60 : classSoulName === rightName ? 160 : null;
  const surgeonCx = surgeonName === leftName ? 60 : surgeonName === rightName ? 160 : null;
  const snowWhiteCx =
    snowWhiteName === leftName ? 60 : snowWhiteName === rightName ? 160 : null;
  const beautyCx = beautyName === leftName ? 60 : beautyName === rightName ? 160 : null;
  const caricaturistCx =
    caricaturistName === leftName ? 60 : caricaturistName === rightName ? 160 : null;
  const effortlessGeniusCx =
    effortlessGeniusName === leftName ? 60 : effortlessGeniusName === rightName ? 160 : null;
  const princessCx =
    princessName === leftName ? 60 : princessName === rightName ? 160 : null;
  const daydreamerCx =
    daydreamerName === leftName ? 60 : daydreamerName === rightName ? 160 : null;
  const mathematicianCx =
    mathematicianName === leftName ? 60 : mathematicianName === rightName ? 160 : null;
  const physicistCx =
    physicistName === leftName ? 60 : physicistName === rightName ? 160 : null;
  const bookwormCx =
    bookwormName === leftName ? 60 : bookwormName === rightName ? 160 : null;
  const exorcistCx =
    exorcistName === leftName ? 60 : exorcistName === rightName ? 160 : null;
  return (
    <svg
      viewBox="0 0 220 124"
      className="bench-svg"
      preserveAspectRatio="xMidYMid meet"
      aria-label={`Bench: ${leftName} and ${rightName}`}
    >
      <StudentSilhouette name={leftName} cx={60} />
      <StudentSilhouette name={rightName} cx={160} />
      <StudentLegs name={leftName} cx={60} sprint={leftName === sportyName} />
      <StudentLegs name={rightName} cx={160} sprint={rightName === sportyName} />
      {/* Bench desk drawn on top of avatars so lower body is hidden */}
      <rect
        x={3}
        y={55}
        width={214}
        height={34}
        fill="url(#bench-wood)"
        stroke="#3a2410"
        strokeWidth={2}
        rx={2}
      />
      {/* Top highlight on the desk */}
      <rect x={3} y={55} width={214} height={3} fill="#c89860" opacity={0.45} />
      {/* Bottom shelf */}
      <rect x={3} y={87} width={214} height={5} fill="#5a3a1a" />
      {/* Name plates */}
      <text
        x={18}
        y={73}
        fontSize={9}
        fill="rgba(255,255,255,0.92)"
        fontFamily="Georgia, serif"
      >
        {leftName}
      </text>
      <text
        x={202}
        y={73}
        textAnchor="end"
        fontSize={9}
        fill="rgba(255,255,255,0.92)"
        fontFamily="Georgia, serif"
      >
        {rightName}
      </text>
      {fireId != null && <PaperFire key={fireId} />}
      {hoopsId != null && <HoopsScene key={hoopsId} />}
      {guitarId != null && <GuitarScene key={guitarId} />}
      {goal && <FootballGoal key={goal.id} mirrored={goal.mirrored} />}
      {football && <FootballBall key={football.id} shot={football} />}
      {punkCx != null && <PunkCostume cx={punkCx} />}
      {metal && leftName !== punkName && leftName !== exorcistName && <MetalHorns cx={60} />}
      {metal && rightName !== punkName && rightName !== exorcistName && <MetalHorns cx={160} />}
      {bookwormCx != null && <BookPile cx={bookwormCx} />}
      {physicistCx != null && <PhysicsGenius cx={physicistCx} />}
      {mathematicianCx != null && <MathGenius cx={mathematicianCx} />}
      {daydreamerCx != null && <HeadInClouds cx={daydreamerCx} />}
      {princessCx != null && <Princess cx={princessCx} />}
      {effortlessGeniusCx != null && <EffortlessGenius cx={effortlessGeniusCx} />}
      {caricaturistCx != null && <Caricaturist cx={caricaturistCx} />}
      {djCx != null && <DjSet cx={djCx} />}
      {sportyCx != null && <Sporty cx={sportyCx} />}
      {classSoulCx != null && classSoulName && <ClassSoul cx={classSoulCx} name={classSoulName} />}
      {surgeonCx != null && <Surgeon cx={surgeonCx} />}
      {snowWhiteCx != null && <SnowWhite cx={snowWhiteCx} />}
      {beautyCx != null && <BeautyQueen cx={beautyCx} />}
      {admirers?.has(leftName) && <LoveStruck cx={60} name={leftName} />}
      {admirers?.has(rightName) && <LoveStruck cx={160} name={rightName} />}
      {exorcistCx != null && <Crucifix cx={exorcistCx} />}
      {captainCx != null && (
        <>
          <CaptainHat cx={captainCx} />
          {/* Bottle goes on the outer end of the desk, clear of the photos */}
          <RumBottle x={captainCx + (captainCx > 110 ? 41 : -41)} />
        </>
      )}
      {/* Transparent hover hit-areas over each student's head/shoulders */}
      <rect
        x={28}
        y={2}
        width={64}
        height={52}
        fill="transparent"
        style={{ cursor: "pointer" }}
        data-student={leftName}
        onMouseEnter={(e) => onHover(leftName, e)}
        onMouseMove={(e) => onHover(leftName, e)}
        onMouseLeave={onLeave}
        onClick={() => onStudentClick(leftName)}
      />
      <rect
        x={128}
        y={2}
        width={64}
        height={52}
        fill="transparent"
        style={{ cursor: "pointer" }}
        data-student={rightName}
        onMouseEnter={(e) => onHover(rightName, e)}
        onMouseMove={(e) => onHover(rightName, e)}
        onMouseLeave={onLeave}
        onClick={() => onStudentClick(rightName)}
      />
    </svg>
  );
}

function GraffitiTag() {
  return (
    <svg
      viewBox="0 0 220 110"
      className="graffiti-tag"
      aria-label="XII A"
    >
      {/* Spray-paint dust */}
      <circle cx={12} cy={20} r={1.8} fill="#1a1a1a" opacity={0.45} />
      <circle cx={205} cy={28} r={1.2} fill="#e6293a" opacity={0.6} />
      <circle cx={195} cy={85} r={1} fill="#1a1a1a" opacity={0.4} />
      <circle cx={28} cy={92} r={1.4} fill="#e6293a" opacity={0.55} />
      <circle cx={110} cy={12} r={0.8} fill="#1a1a1a" opacity={0.35} />
      <circle cx={60} cy={100} r={0.9} fill="#1a1a1a" opacity={0.45} />
      <circle cx={170} cy={18} r={0.7} fill="#e6293a" opacity={0.5} />
      <circle cx={150} cy={102} r={1.1} fill="#1a1a1a" opacity={0.4} />
      {/* Drips */}
      <path
        d="M 55 78 L 55 100 Q 55 104 57 104 Q 59 104 59 100 L 59 80 Z"
        fill="#e6293a"
        stroke="#1a1a1a"
        strokeWidth={1}
      />
      <path
        d="M 138 72 L 138 92"
        stroke="#e6293a"
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <circle cx={138} cy={95} r={2.4} fill="#e6293a" stroke="#1a1a1a" strokeWidth={0.6} />
      <path
        d="M 175 74 L 175 84"
        stroke="#1a1a1a"
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      {/* Shadow text layer */}
      <text
        x={22}
        y={70}
        fontFamily="'Bangers', 'Impact', 'Arial Black', sans-serif"
        fontSize={68}
        fontWeight={900}
        fill="#1a1a1a"
        transform="rotate(-6 22 70) translate(5 5)"
        letterSpacing="2"
      >
        XII A
      </text>
      {/* Main red fill with thick black outline */}
      <text
        x={22}
        y={70}
        fontFamily="'Bangers', 'Impact', 'Arial Black', sans-serif"
        fontSize={68}
        fontWeight={900}
        fill="#e6293a"
        stroke="#1a1a1a"
        strokeWidth={3}
        paintOrder="stroke"
        transform="rotate(-6 22 70)"
        letterSpacing="2"
      >
        XII A
      </text>
      {/* Inner highlight */}
      <text
        x={20}
        y={66}
        fontFamily="'Bangers', 'Impact', 'Arial Black', sans-serif"
        fontSize={68}
        fontWeight={900}
        fill="#ffeb3b"
        opacity={0.55}
        transform="rotate(-6 20 66)"
        letterSpacing="2"
      >
        XII A
      </text>
      {/* Underline scribble */}
      <path
        d="M 24 92 Q 60 96 110 90 Q 150 86 195 92"
        stroke="#1a1a1a"
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        transform="rotate(-3 110 90)"
      />
      {/* Lightning bolt accent — rock vibe */}
      <path
        d="M 200 35 L 196 50 L 202 50 L 198 65 L 210 47 L 204 47 L 208 35 Z"
        fill="#ffeb3b"
        stroke="#1a1a1a"
        strokeWidth={1}
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TeacherAvatar({
  onHover,
  onLeave,
}: {
  onHover: (name: string, e: MouseEvent, below?: boolean) => void;
  onLeave: () => void;
}) {
  const base = import.meta.env.BASE_URL;
  return (
    <img
      className="teacher-figure"
      src={`${base}img/dinu.png`}
      alt="Teacher: Dinu Ioan"
      draggable={false}
      style={{ cursor: "pointer" }}
      onMouseEnter={(e) => onHover("Dinu Ioan", e, true)}
      onMouseMove={(e) => onHover("Dinu Ioan", e, true)}
      onMouseLeave={onLeave}
    />
  );
}

function SharedDefs() {
  // Hoisted to document scope so every bench SVG can reference the gradient
  // by URL without colliding IDs.
  return (
    <svg width={0} height={0} className="svg-defs" aria-hidden>
      <defs>
        <linearGradient id="bench-wood" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#b07a4a" />
          <stop offset="100%" stopColor="#825327" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function Classroom() {
  const [hover, setHover] = useState<HoverState | null>(null);

  const onHover = (name: string, e: MouseEvent, below?: boolean) =>
    setHover({ x: e.clientX, y: e.clientY, name, below });
  const onLeave = () => setHover(null);

  const classroomRef = useRef<HTMLDivElement>(null);
  const nextPaperId = useRef(0);
  const [throwing, setThrowing] = useState(false);
  const [papers, setPapers] = useState<Paper[]>([]);

  // Clicking the thrower toggles the paper barrage on and off.
  const nextBubbleId = useRef(0);
  const [bubble, setBubble] = useState<Bubble | null>(null);

  const showBubble = (
    name: string,
    lines: readonly string[],
    durationMs: number,
    song?: boolean,
  ) => {
    const root = classroomRef.current;
    const head = root?.querySelector(`[data-student="${name}"]`)?.getBoundingClientRect();
    if (!root || !head) return;
    const origin = root.getBoundingClientRect();
    setBubble({
      id: nextBubbleId.current++,
      name,
      x: head.left + head.width / 2 - origin.left,
      y: head.top - origin.top,
      lines,
      durationMs,
      song,
    });
  };

  const onStudentClick = (name: string) => {
    if (name === PAPER_THROWER) setThrowing((on) => !on);
    if (BUBBLE_SPEAKERS.has(name)) showBubble(name, [BUBBLE_TEXT], BUBBLE_DURATION_MS);
    if (name === GUITARIST) {
      setGuitarId((id) => (id ?? 0) + 1);
      showBubble(name, SONG_LINES, SONG_DURATION_MS, true);
    }
    if (name === ARSONIST) setFireId((id) => (id ?? 0) + 1);
    if (name === CAPTAIN) setCaptainOn((on) => !on);
    if (name === PUNK) setPunkOn((on) => !on);
    if (name === BOOKWORM) setBooksOn((on) => !on);
    if (name === PHYSICIST) setPhysicsOn((on) => !on);
    if (name === MATHEMATICIAN) setMathOn((on) => !on);
    if (name === DAYDREAMER) setCloudsOn((on) => !on);
    if (name === PRINCESS) setPrincessOn((on) => !on);
    if (name === EFFORTLESS_GENIUS) setEffortlessOn((on) => !on);
    if (name === CARICATURIST) setCaricaturesOn((on) => !on);
    if (name === DJ) setDjOn((on) => !on);
    if (name === SPORTY) setSportyOn((on) => !on);
    if (name === CLASS_SOUL) setPartyOn((on) => !on);
    if (name === SURGEON) setSurgeonOn((on) => !on);
    if (name === SNOW_WHITE) setSnowWhiteOn((on) => !on);
    if (name === BEAUTY) toggleAdmirers();
    if (FOOTBALL_RIVALS.includes(name)) {
      kickFootball(name, FOOTBALL_RIVALS.find((rival) => rival !== name)!);
    }
    if (name === EXORCIST) {
      // Shout only when the crucifix comes out, not when it is put away
      if (!exorcistOn) showBubble(name, [EXORCISM_TEXT], BUBBLE_DURATION_MS);
      setExorcistOn(!exorcistOn);
    }
    if (name === BALLER) setHoopsId((id) => (id ?? 0) + 1);
  };

  const [hoopsId, setHoopsId] = useState<number | null>(null);

  // Pack the hoop away once the shot has gone in.
  useEffect(() => {
    if (hoopsId == null) return;
    const timer = setTimeout(() => setHoopsId(null), HOOPS_DURATION_MS);
    return () => clearTimeout(timer);
  }, [hoopsId]);

  const [captainOn, setCaptainOn] = useState(false);
  const [punkOn, setPunkOn] = useState(false);
  const [booksOn, setBooksOn] = useState(false);
  const [physicsOn, setPhysicsOn] = useState(false);
  const [mathOn, setMathOn] = useState(false);
  const [cloudsOn, setCloudsOn] = useState(false);
  const [princessOn, setPrincessOn] = useState(false);
  const [effortlessOn, setEffortlessOn] = useState(false);
  const [caricaturesOn, setCaricaturesOn] = useState(false);
  const [djOn, setDjOn] = useState(false);
  const [sportyOn, setSportyOn] = useState(false);
  const [partyOn, setPartyOn] = useState(false);
  const [surgeonOn, setSurgeonOn] = useState(false);
  const [snowWhiteOn, setSnowWhiteOn] = useState(false);
  const [exorcistOn, setExorcistOn] = useState(false);

  const [fireId, setFireId] = useState<number | null>(null);

  // Sweep away the ashes once the fire animation has played out.
  useEffect(() => {
    if (fireId == null) return;
    const timer = setTimeout(() => setFireId(null), FIRE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [fireId]);

  // Each new bubble restarts the countdown before it disappears.
  useEffect(() => {
    if (!bubble) return;
    const timer = setTimeout(() => setBubble(null), bubble.durationMs);
    return () => clearTimeout(timer);
  }, [bubble]);

  const [football, setFootball] = useState<FootballShot | null>(null);

  // Measure how far the goal owner's bench is from the footballer's, so the
  // shot lands in the goal whatever the layout (grid or stacked on mobile).
  const kickFootball = (kicker: string, goalOwner: string) => {
    const benchRect = (name: string) =>
      classroomRef.current
        ?.querySelector<SVGRectElement>(`[data-student="${name}"]`)
        ?.ownerSVGElement?.getBoundingClientRect();
    const from = benchRect(kicker);
    const to = benchRect(goalOwner);
    if (!from || !to) return;
    const unit = from.width / BENCH_VIEW_WIDTH;
    const dx = (to.left - from.left) / unit;
    setFootball((shot) => ({
      id: (shot?.id ?? 0) + 1,
      kicker,
      goalOwner,
      dx,
      dy: (to.top - from.top) / unit,
      // Does the ball reach the goal travelling leftwards?
      mirrored: dx + FOOTBALL_GOAL_LEFT + FOOTBALL_GOAL_DEPTH / 2 < FOOTBALL_KICK_X,
    }));
  };

  // Clear the pitch once the goal has been celebrated.
  useEffect(() => {
    if (!football) return;
    const timer = setTimeout(() => setFootball(null), FOOTBALL_DURATION_MS);
    return () => clearTimeout(timer);
  }, [football]);

  const [guitarId, setGuitarId] = useState<number | null>(null);

  // Put the guitar down when the song is over.
  useEffect(() => {
    if (guitarId == null) return;
    const timer = setTimeout(() => setGuitarId(null), SONG_DURATION_MS);
    return () => clearTimeout(timer);
  }, [guitarId]);

  // Launch one projectile from one student's head to another's. Only touches
  // refs and state setters, so it is safe to call from the interval effects.
  const launch = (fromName: string, toName: string, kind?: Paper["kind"]) => {
    const root = classroomRef.current;
    const from = root?.querySelector(`[data-student="${fromName}"]`)?.getBoundingClientRect();
    const to = root?.querySelector(`[data-student="${toName}"]`)?.getBoundingClientRect();
    if (!root || !from || !to) return;
    // Measured on every throw so the aim survives resizes and reflows.
    const origin = root.getBoundingClientRect();
    const x = from.left + from.width * 0.5 - origin.left;
    const y = from.top + from.height * (kind === "heart" ? 0.3 : 0.7) - origin.top;
    const targetX = to.left + to.width * (0.25 + Math.random() * 0.5) - origin.left;
    const targetY = to.top + to.height * (0.2 + Math.random() * 0.5) - origin.top;
    const paper: Paper = {
      id: nextPaperId.current++,
      x,
      y,
      dx: targetX - x,
      dy: targetY - y,
      arc: (Math.random() - 0.5) * (kind ? 90 : 50),
      bounce: (Math.random() - 0.5) * 50,
      spin: (Math.random() < 0.5 ? -1 : 1) * (360 + Math.random() * 540),
      kind,
    };
    setPapers((flying) => [...flying, paper]);
  };

  useEffect(() => {
    if (!throwing) return;
    const timer = setInterval(() => launch(PAPER_THROWER, PAPER_TARGET), PAPER_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [throwing]);

  // The boys smitten with the class beauty this time round, or null when off.
  const [admirers, setAdmirers] = useState<ReadonlySet<string> | null>(null);

  // Clicking the beauty toggles the lovestruck boys; each time a different
  // random lot of them (a bit more than half) falls for her.
  const toggleAdmirers = () => {
    if (admirers) return setAdmirers(null);
    const count = Math.ceil(BOYS.length * ADMIRER_SHARE);
    // The rest of the quota is drawn at random from the undecided boys.
    const undecided = BOYS.filter(
      (name) => !ALWAYS_ADMIRERS.includes(name) && !NEVER_ADMIRERS.includes(name),
    ).sort(() => Math.random() - 0.5);
    setAdmirers(
      new Set([...ALWAYS_ADMIRERS, ...undecided.slice(0, count - ALWAYS_ADMIRERS.length)]),
    );
  };

  // While her party is on, sparks keep flying from her to random classmates.
  useEffect(() => {
    if (!partyOn) return;
    const timer = setInterval(
      () =>
        launch(CLASS_SOUL, CLASSMATES[Math.floor(Math.random() * CLASSMATES.length)], "spark"),
      SPARK_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [partyOn]);

  // While she is admired, hearts keep flying to her from random admirers.
  useEffect(() => {
    if (!admirers) return;
    const smitten = [...admirers];
    const timer = setInterval(
      () => launch(smitten[Math.floor(Math.random() * smitten.length)], BEAUTY, "heart"),
      HEART_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [admirers]);

  const removePaper = (id: number) =>
    setPapers((flying) => flying.filter((paper) => paper.id !== id));

  return (
    <div
      className={["classroom", partyOn && "classroom--party", punkOn && "classroom--metal"]
        .filter(Boolean)
        .join(" ")}
      ref={classroomRef}
    >
      <SharedDefs />
      <GraffitiTag />
      <div className="teacher-zone">
        <div className="teacher-desk-wrap">
          <TeacherAvatar onHover={onHover} onLeave={onLeave} />
          <div className="teacher-desk">Dinu Ioan</div>
        </div>
      </div>

      <div className="bench-rows">
        <div className="bench-row bench-row-solo">
          <BenchView
            leftName={TOP_LEFT_BENCH[0]}
            rightName={TOP_LEFT_BENCH[1]}
            onHover={onHover}
            onLeave={onLeave}
            onStudentClick={onStudentClick}
            effortlessGeniusName={effortlessOn ? EFFORTLESS_GENIUS : null}
            metal={punkOn}
          />
        </div>
        {BENCH_ROWS.map((row, rowIdx) => (
          <div className="bench-row" key={rowIdx}>
            {row.map(([left, right], colIdx) => (
              <BenchView
                key={colIdx}
                leftName={left}
                rightName={right}
                onHover={onHover}
                onLeave={onLeave}
                onStudentClick={onStudentClick}
                fireId={left === ARSONIST || right === ARSONIST ? fireId : null}
                captainName={captainOn ? CAPTAIN : null}
                punkName={punkOn ? PUNK : null}
                metal={punkOn}
                bookwormName={booksOn ? BOOKWORM : null}
                physicistName={physicsOn ? PHYSICIST : null}
                mathematicianName={mathOn ? MATHEMATICIAN : null}
                daydreamerName={cloudsOn ? DAYDREAMER : null}
                princessName={princessOn ? PRINCESS : null}
                caricaturistName={caricaturesOn ? CARICATURIST : null}
                djName={djOn ? DJ : null}
                sportyName={sportyOn ? SPORTY : null}
                classSoulName={partyOn ? CLASS_SOUL : null}
                surgeonName={surgeonOn ? SURGEON : null}
                snowWhiteName={snowWhiteOn ? SNOW_WHITE : null}
                beautyName={admirers ? BEAUTY : null}
                admirers={admirers}
                exorcistName={exorcistOn ? EXORCIST : null}
                hoopsId={left === BALLER ? hoopsId : null}
                guitarId={left === GUITARIST ? guitarId : null}
                football={left === football?.kicker ? football : null}
                goal={left === football?.goalOwner ? football : null}
              />
            ))}
          </div>
        ))}
      </div>

      {punkOn && (
        <>
          <div className="metal-banner">PUNK&apos;S NOT DEAD</div>
          {METAL_BATS.map((bat, i) => (
            <span
              className={bat.reverse ? "metal-bat metal-bat--reverse" : "metal-bat"}
              style={{ top: bat.top, animationDuration: bat.duration, animationDelay: bat.delay }}
              key={i}
            >
              <span className="metal-bat-flap">🦇</span>
            </span>
          ))}
          {METAL_BOLTS.map((bolt, i) => (
            <span
              className="metal-bolt"
              style={{ left: bolt.left, top: bolt.top, animationDelay: bolt.delay }}
              key={i}
            >
              ⚡
            </span>
          ))}
        </>
      )}

      {partyOn &&
        CONFETTI.map((piece, i) => (
          <span
            className="confetti"
            style={{
              left: piece.left,
              background: piece.color,
              animationDuration: piece.duration,
              animationDelay: piece.delay,
            }}
            key={i}
          />
        ))}

      {papers.map((paper) => (
        <span
          key={paper.id}
          className={
            paper.kind === "heart"
              ? "flying-heart"
              : paper.kind === "spark"
                ? "flying-heart flying-heart--spark"
                : "paper-ball"
          }
          style={
            {
              left: paper.x,
              top: paper.y,
              "--dx": `${paper.dx}px`,
              "--dy": `${paper.dy}px`,
              "--arc": `${paper.arc}px`,
              "--bounce": `${paper.bounce}px`,
              "--spin": `${paper.spin}deg`,
            } as CSSProperties
          }
          onAnimationEnd={() => removePaper(paper.id)}
        >
          {paper.kind === "heart" ? "♥" : paper.kind === "spark" ? "★" : null}
        </span>
      ))}

      {bubble && (
        <div
          key={bubble.id}
          className={bubble.song ? "speech-bubble speech-bubble--song" : "speech-bubble"}
          style={{ left: bubble.x, top: bubble.y }}
          role="status"
        >
          {bubble.lines.map((line, i) => (
            <span
              className="speech-bubble-line"
              // Sung lines come in one at a time
              style={bubble.song ? { animationDelay: `${0.7 + i * 2.4}s` } : undefined}
              key={i}
            >
              {bubble.song ? `♪ ${line}` : line}
            </span>
          ))}
        </div>
      )}

      {/* The avatar popup would cover the bubble, so it yields while one is up */}
      {hover && hover.name !== bubble?.name && (
        <div
          className={hover.below ? "avatar-popup avatar-popup--below" : "avatar-popup"}
          style={{ left: hover.x, top: hover.y }}
          role="tooltip"
        >
          <AvatarImage key={hover.name} name={hover.name} />
          <span className="avatar-popup-name">{hover.name}</span>
        </div>
      )}
    </div>
  );
}
